import React, { useState } from 'react';
import useAppStore from '../store/index';
import { AppSettings, TeamMember } from '../store/appStore';
import { db, storage } from '../config/firebase'; 
import { doc, setDoc } from 'firebase/firestore'; 
import { executeGlobalMassFreeze, executeGlobalMassUnfreeze } from '../config/firebaseSync'; 

import UI from '../components/UI';
import EditableField from '../components/EditableField';
import { X, Plus, Edit2, ArrowUp, ArrowDown, Save, Trash2, Upload, Download, Loader2, Eye, EyeOff, ListTree } from 'lucide-react';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import type { DownloadItem, DownloadFileType } from '../store/types';
import { LinkifiedText } from '../utils/linkifyText';

const INVOICE_PRESETS: Record<string, any> = {
    'nelly': {
        invoicePrefix: 'KN',
        invoiceLogoUrl: 'skaadi-nelly.png',
        invoiceCompanyName: 'skaadi HOLISTIC BEAUTY',
        invoiceStreet: 'Hallerstrasse 48',
        invoiceCity: '74549 Wolpertshausen',
        invoiceTaxId: 'USt-Id: DE363442375',
        invoiceIban: 'DE95 6209 1800 0377 9770 12',
        invoiceBic: 'GENOGES1VHL',
        invoiceReverseCharge: false
    },
    'martina': {
        invoicePrefix: 'KM',
        invoiceLogoUrl: 'friseurteam-logo.png',
        invoiceCompanyName: 'Friseur Team',
        invoiceStreet: 'Feldstraße 19',
        invoiceCity: '33609 Bielefeld',
        invoiceTaxId: '305/5037/4231 | USt-Id: DE298727966',
        invoiceIban: 'DE63 4805 0161 0065 0350 32',
        invoiceBic: 'SPBIDE3BXXX',
        invoiceReverseCharge: false
    },
    'jean': {
        invoicePrefix: 'KJ',
        invoiceLogoUrl: 'jean-jacqueline-logo.png',
        invoiceCompanyName: 'Jean-Jacqueline Unisex-Friseur',
        invoiceStreet: 'Rudolf-Schwander-Straße 7',
        invoiceCity: '34117 Kassel',
        invoiceTaxId: '026/3766/0323 | USt-Id: DE317758618',
        invoiceIban: 'DE45 5204 0021 0265 3186 00',
        invoiceBic: 'COBADEFFXXX',
        invoiceReverseCharge: false
    },
    'jacqueline': {
        invoicePrefix: 'KJ',
        invoiceLogoUrl: 'jean-jacqueline-logo.png', 
        invoiceCompanyName: 'Jean-Jacqueline Unisex-Friseur',
        invoiceStreet: 'Rudolf-Schwander-Straße 7',
        invoiceCity: '34117 Kassel',
        invoiceTaxId: '026/3766/0323 | USt-Id: DE317758618',
        invoiceIban: 'DE45 5204 0021 0265 3186 00',
        invoiceBic: 'COBADEFFXXX',
        invoiceReverseCharge: false
    },
    'omar': {
        invoicePrefix: 'SO',
        invoiceLogoUrl: '',
        invoiceCompanyName: 'Omar Hanafy',
        invoiceStreet: '4 Gamal El-Din Dewedar von Abbas El-Akkad Str.',
        invoiceCity: '11787 Kairo, Ägypten',
        invoiceTaxId: '-',
        invoiceIban: 'EG65 0025 0074 0000 0074 1866 8511 0',
        invoiceBic: 'EBBKEGCX',
        invoiceReverseCharge: true
    }
};

const TeamManagement = () => {
    const { teamRegistry, addTeamMember, updateTeamMember, deleteTeamMember } = useAppStore(state => ({
        teamRegistry: state.teamRegistry || [],
        addTeamMember: state.addTeamMember,
        updateTeamMember: state.updateTeamMember,
        deleteTeamMember: state.deleteTeamMember,
    }));

    const [editingMember, setEditingMember] = useState<Partial<TeamMember> & any | null>(null);

    const handleSave = () => {
        if (!editingMember || !editingMember.name) return;
        
        const billingFactor = parseFloat(editingMember.billingFactor?.toString().replace(',', '.') || '0');
        const fixSalary = parseFloat(editingMember.fixedSalary?.toString().replace(',', '.') || '0');

        const memberData: any = {
            name: editingMember.name || '',
            role: editingMember.role || '',
            imageUrl: editingMember.imageUrl || '',
            description: editingMember.description || '',
            email: editingMember.email || '', 
            phone: editingMember.phone || '',
            category: editingMember.category || 'coach',
            
            billingFactor: billingFactor,
            fixedSalary: fixSalary,

            calendarLinkBasic: editingMember.calendarLinkBasic || '',
            calendarLinkInsider: editingMember.calendarLinkInsider || '',
            calendarLinkTeam: editingMember.calendarLinkTeam || '',

            invoicePrefix: editingMember.invoicePrefix || '',
            invoiceLogoUrl: editingMember.invoiceLogoUrl || '',
            invoiceCompanyName: editingMember.invoiceCompanyName || '',
            invoiceStreet: editingMember.invoiceStreet || '',
            invoiceCity: editingMember.invoiceCity || '',
            invoiceTaxId: editingMember.invoiceTaxId || '',
            invoiceIban: editingMember.invoiceIban || '',
            invoiceBic: editingMember.invoiceBic || '',
            invoiceReverseCharge: editingMember.invoiceReverseCharge || false,
        };

        if (editingMember.id) {
            updateTeamMember({ ...memberData, id: editingMember.id });
        } else {
            addTeamMember(memberData);
        }
        setEditingMember(null);
    };

    const handleAddNew = () => {
        setEditingMember({
            name: '', role: '', imageUrl: '', description: '', email: '', phone: '', category: 'coach',
            billingFactor: 0, fixedSalary: 0,
            calendarLinkBasic: '', calendarLinkInsider: '', calendarLinkTeam: '',
            invoicePrefix: '', invoiceLogoUrl: '', invoiceCompanyName: '', invoiceStreet: '', invoiceCity: '', 
            invoiceTaxId: '', invoiceIban: '', invoiceBic: '', invoiceReverseCharge: false,
        });
    };

    const handleEdit = (member: TeamMember) => {
        setEditingMember({...member});
    };

    const handleApplyPreset = (presetKey: string) => {
        setEditingMember((prev: any) => ({
            ...prev,
            ...INVOICE_PRESETS[presetKey]
        }));
    };

    const renderForm = () => {
        if (!editingMember) return null;

        const handleChange = (field: string, value: any) => {
            setEditingMember((prev: any) => prev ? { ...prev, [field]: value } : null);
        };

        const currentNameLower = (editingMember.name || '').toLowerCase();
        const matchedPresetKey = Object.keys(INVOICE_PRESETS).find(key => currentNameLower.includes(key));

        return (
            <div className="bg-black p-6 rounded-lg border border-slate-700 mt-4 space-y-4 shadow-2xl animate-fade-in relative">
                <div className="absolute top-0 left-0 w-1 h-full bg-[#E6C299] rounded-l-lg"></div>
                <h4 className="text-sm font-bold text-[#E6C299] uppercase tracking-wider mb-4 border-b border-gray-800 pb-2">
                    {editingMember.id ? 'Mitglied bearbeiten' : 'Neues Mitglied'}
                </h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <span className="text-[10px] text-gray-400 uppercase font-bold mb-1 block">Name *</span>
                        <UI.Input value={editingMember.name || ''} onChange={(e) => handleChange('name', e.target.value)} placeholder="Name des Mitarbeiters" />
                    </div>
                    <div>
                        <span className="text-[10px] text-gray-400 uppercase font-bold mb-1 block">Rolle</span>
                        <UI.Input value={editingMember.role || ''} onChange={(e) => handleChange('role', e.target.value)} placeholder="z.B. Gründer" />
                    </div>

                    <div className="md:col-span-2 pt-4 border-t border-slate-800 mt-2">
                        <h5 className="text-[10px] font-black text-blue-400 uppercase tracking-widest mb-3">Spezifische Werte (Abrechnung Mandant & Fixkosten)</h5>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-[#0a0f1c] p-4 rounded border border-slate-800">
                            <div>
                                <span className="text-[10px] text-gray-400 uppercase font-bold mb-1 block">Abrechnungs-Faktor Mandant (z.B. 100, 50, 30)</span>
                                <UI.Input value={editingMember.billingFactor || 0} onChange={(e) => handleChange('billingFactor', e.target.value)} placeholder="0" />
                                <p className="text-[9px] text-slate-500 mt-1">Wie viel wird dem Mandanten in Rechnung gestellt? Dies hat KEINEN Einfluss auf die interne Gewinnverteilung.</p>
                            </div>
                            <div>
                                <span className="text-[10px] text-gray-400 uppercase font-bold mb-1 block">Fix-Gehalt in € (z.B. für Support / Omar / Jean)</span>
                                <UI.Input value={editingMember.fixedSalary || 0} onChange={(e) => handleChange('fixedSalary', e.target.value)} placeholder="0" />
                                <p className="text-[9px] text-slate-500 mt-1">Dieses Fixgehalt wird in der globalen Matrix als Vorab-Kostenfaktor einkalkuliert.</p>
                            </div>
                        </div>
                    </div>

                    <div>
                        <span className="text-[10px] text-gray-400 uppercase font-bold mb-1 block">E-Mail</span>
                        <UI.Input value={editingMember.email || ''} onChange={(e) => handleChange('email', e.target.value)} placeholder="mitarbeiter@firma.de" type="email" />
                    </div>
                    <div>
                        <span className="text-[10px] text-gray-400 uppercase font-bold mb-1 block">Telefon</span>
                        <UI.Input value={editingMember.phone || ''} onChange={(e) => handleChange('phone', e.target.value)} placeholder="+49 ..." />
                    </div>
                    <div>
                        <span className="text-[10px] text-gray-400 uppercase font-bold mb-1 block">Kategorie</span>
                        <UI.Select value={editingMember.category || 'coach'} onChange={(e) => handleChange('category', e.target.value)}>
                            <option value="coach">Coach</option>
                            <option value="webdesign">Webdesign</option>
                            <option value="support">Support</option>
                        </UI.Select>
                    </div>
                    <div>
                        <span className="text-[10px] text-gray-400 uppercase font-bold mb-1 block">Bild URL (Profil)</span>
                        <UI.Input value={editingMember.imageUrl || ''} onChange={(e) => handleChange('imageUrl', e.target.value)} placeholder="https://imgur.com/..." />
                    </div>
                    
                    <div className="md:col-span-2 pt-4 border-t border-slate-800 mt-2">
                        <h5 className="text-[10px] font-black text-emerald-400 uppercase tracking-widest mb-3">Booking-Links (Kalender-Integration)</h5>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-emerald-950/10 p-4 rounded border border-emerald-900/30">
                            <div>
                                <span className="text-[10px] text-emerald-200/70 uppercase font-bold mb-1 block">Allgemein / Solo (30 Min)</span>
                                <UI.Input value={editingMember.calendarLinkBasic || ''} onChange={(e) => handleChange('calendarLinkBasic', e.target.value)} placeholder="https://calendly.com/..." />
                                <p className="text-[9px] text-slate-500 mt-1">Für allgemeine Rückfragen sowie Solo-Mandanten.</p>
                            </div>
                            <div>
                                <span className="text-[10px] text-emerald-200/70 uppercase font-bold mb-1 block">Team & MA-Auswertung (60 Min)</span>
                                <UI.Input value={editingMember.calendarLinkInsider || ''} onChange={(e) => handleChange('calendarLinkInsider', e.target.value)} placeholder="https://calendly.com/..." />
                                <p className="text-[9px] text-slate-500 mt-1">Haupt-Meeting für Team / Team-Plus und MA-Auswertung.</p>
                            </div>
                            <div>
                                <span className="text-[10px] text-emerald-200/70 uppercase font-bold mb-1 block">Großes Meeting (90 Min)</span>
                                <UI.Input value={editingMember.calendarLinkTeam || ''} onChange={(e) => handleChange('calendarLinkTeam', e.target.value)} placeholder="https://calendly.com/..." />
                                <p className="text-[9px] text-slate-500 mt-1">Für große Meetings (wird nicht in System-Mails gesendet).</p>
                            </div>
                        </div>
                    </div>

                    <div className="md:col-span-2 pt-4 border-t border-slate-800 mt-2">
                        <div className="flex justify-between items-center mb-3">
                            <h5 className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Rechnungs-Daten (Für Coach-Dashboard)</h5>
                            {matchedPresetKey && (
                                <button 
                                    onClick={() => handleApplyPreset(matchedPresetKey)}
                                    type="button" 
                                    className="text-[10px] bg-emerald-600/20 text-emerald-400 border border-emerald-500/50 px-3 py-1 rounded hover:bg-emerald-600/40 transition-colors flex items-center gap-2 font-bold uppercase tracking-widest"
                                >
                                    <span>🪄</span> Daten für {matchedPresetKey} einsetzen
                                </button>
                            )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-slate-900/50 p-4 rounded border border-slate-800">
                            <div>
                                <span className="text-[10px] text-gray-400 uppercase font-bold mb-1 block">Rechnungs-Präfix</span>
                                <UI.Input value={editingMember.invoicePrefix || ''} onChange={(e) => handleChange('invoicePrefix', e.target.value)} placeholder="z.B. CF, NK, MG" />
                            </div>
                            <div>
                                <span className="text-[10px] text-gray-400 uppercase font-bold mb-1 block">Logo-Dateiname (Assets)</span>
                                <UI.Input value={editingMember.invoiceLogoUrl || ''} onChange={(e) => handleChange('invoiceLogoUrl', e.target.value)} placeholder="z.B. skaadi-nelly.png" />
                            </div>
                            <div className="md:col-span-2 mt-2 border-t border-slate-800 pt-3">
                                <span className="text-[10px] text-gray-400 uppercase font-bold mb-1 block">Firmenname / Rechnungssteller</span>
                                <UI.Input value={editingMember.invoiceCompanyName || ''} onChange={(e) => handleChange('invoiceCompanyName', e.target.value)} placeholder="z.B. Friseur Team / skaadi" />
                            </div>
                            <div>
                                <span className="text-[10px] text-gray-400 uppercase font-bold mb-1 block">Straße & Hausnummer</span>
                                <UI.Input value={editingMember.invoiceStreet || ''} onChange={(e) => handleChange('invoiceStreet', e.target.value)} placeholder="Feldstraße 19" />
                            </div>
                            <div>
                                <span className="text-[10px] text-gray-400 uppercase font-bold mb-1 block">PLZ & Ort</span>
                                <UI.Input value={editingMember.invoiceCity || ''} onChange={(e) => handleChange('invoiceCity', e.target.value)} placeholder="33609 Bielefeld" />
                            </div>
                            <div>
                                <span className="text-[10px] text-gray-400 uppercase font-bold mb-1 block">Steuernummer / USt-IdNr.</span>
                                <UI.Input value={editingMember.invoiceTaxId || ''} onChange={(e) => handleChange('invoiceTaxId', e.target.value)} placeholder="z.B. DE298727966" />
                            </div>
                            <div></div>
                            <div>
                                <span className="text-[10px] text-gray-400 uppercase font-bold mb-1 block">IBAN</span>
                                <UI.Input value={editingMember.invoiceIban || ''} onChange={(e) => handleChange('invoiceIban', e.target.value)} placeholder="DE..." />
                            </div>
                            <div>
                                <span className="text-[10px] text-gray-400 uppercase font-bold mb-1 block">BIC</span>
                                <UI.Input value={editingMember.invoiceBic || ''} onChange={(e) => handleChange('invoiceBic', e.target.value)} placeholder="GENO..." />
                            </div>
                            <div className="md:col-span-2 pt-2">
                                <label className="flex items-center gap-3 cursor-pointer bg-slate-900 p-3 rounded border border-slate-700">
                                    <input 
                                        type="checkbox" 
                                        checked={editingMember.invoiceReverseCharge || false} 
                                        onChange={(e) => handleChange('invoiceReverseCharge', e.target.checked)} 
                                        className="w-5 h-5 rounded border-slate-600 text-[#E6C299] focus:ring-[#E6C299] bg-[#08090b]" 
                                    />
                                    <div>
                                        <span className="text-sm font-bold text-slate-300">Reverse-Charge (0% MwSt.)</span>
                                        <p className="text-[10px] text-slate-500 mt-0.5">Aktivieren für "Steuerschuldnerschaft des Leistungsempfängers" (z.B. bei Omar/Ausland).</p>
                                    </div>
                                </label>
                            </div>
                        </div>
                    </div>

                    <div className="md:col-span-2">
                        <span className="text-[10px] text-gray-400 uppercase font-bold mb-1 block">Beschreibung</span>
                        <textarea 
                            value={editingMember.description || ''} 
                            onChange={(e) => handleChange('description', e.target.value)} 
                            className="w-full bg-black border border-slate-700 rounded p-2 text-white focus:border-[#E6C299] outline-none transition-colors h-24 text-sm font-sans resize-none"
                            placeholder="Kurzbeschreibung..."
                        />
                    </div>
                </div>
                <div className="flex justify-end gap-3 pt-2 border-t border-gray-800">
                    <UI.Button variant="secondary" onClick={() => setEditingMember(null)}>Abbrechen</UI.Button>
                    <UI.Button variant="primary" onClick={handleSave} disabled={!editingMember?.name}>Speichern</UI.Button>
                </div>
            </div>
        );
    };

    return (
        <>
            <div className="flex justify-between items-center mb-4">
                {!editingMember && (
                    <UI.Button onClick={handleAddNew} className="ml-auto">+ Mitglied hinzufügen</UI.Button>
                )}
            </div>
            {renderForm()}
            <div className="space-y-2 mt-4">
                {teamRegistry.map(member => (
                    <div key={member.id} className="bg-gray-800/50 p-3 rounded border border-gray-700 flex items-center justify-between flex-wrap gap-2 hover:bg-gray-800 transition-colors">
                        <div className="flex items-center gap-4">
                            <img src={member.imageUrl || 'https://placehold.co/40x40/2D2D2D/E6C299?text=?'} alt={member.name} className="w-10 h-10 rounded-full object-cover flex-shrink-0 border border-gray-600" />
                            <div>
                                <p className="font-bold text-white text-sm">{member.name}</p>
                                <p className="text-xs text-[#E6C299] uppercase tracking-wide">{member.role} <span className="text-gray-500">| {member.category}</span></p>
                                
                                <p className="text-[10px] text-red-500 font-mono font-bold bg-red-950/30 px-1.5 py-0.5 rounded w-max mt-1 border border-red-900/50">
                                    ID: {member.id}
                                </p>
                                
                                <div className="flex items-center gap-3 mt-1">
                                    {(member as any).billingFactor > 0 && (
                                        <span className="bg-blue-900/30 text-blue-400 border border-blue-800/50 text-[10px] px-2 py-0.5 rounded font-mono font-bold">Mandanten-Abrechnung: {(member as any).billingFactor}</span>
                                    )}
                                    {(member as any).fixedSalary > 0 && (
                                        <span className="bg-slate-900/50 text-slate-400 border border-slate-700 text-[10px] px-2 py-0.5 rounded font-mono font-bold">Fixgehalt: {(member as any).fixedSalary} €</span>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="flex gap-2 flex-shrink-0">
                            <button onClick={() => handleEdit(member)} className="text-gray-400 hover:text-[#E6C299] text-xs uppercase font-bold px-2 py-1">Bearbeiten</button>
                            <button onClick={() => deleteTeamMember(member.id)} className="text-gray-600 hover:text-red-500 text-xs uppercase font-bold px-2 py-1">Löschen</button>
                        </div>
                    </div>
                ))}
            </div>
        </>
    );
};

const DynamicShareManagement = () => {
    const { team, settings, updateSettings, captureState } = useAppStore(state => ({
        team: state.team || [],
        settings: state.settings,
        updateSettings: state.updateSettings,
        captureState: state.captureState
    }));

    const shareFactors = (settings as any)?.shareFactors || { founder: 35, coach: 15 };
    
    const [localFactors, setLocalFactors] = useState({
        founder: shareFactors.founder.toString(),
        coach: shareFactors.coach.toString()
    });

    const isFounder = React.useCallback((name: string) => name.toLowerCase().includes('christian') || name.toLowerCase().includes('martina'), []);
    
    const foundersList = React.useMemo(() => team.filter(m => isFounder(m.name)), [team, isFounder]);
    const coachesList = React.useMemo(() => team.filter(m => !isFounder(m.name) && (m.category === 'coach' || (m.role && m.role.toLowerCase().includes('coach')))), [team, isFounder]);

    const handleFactorChange = (key: 'founder' | 'coach', value: string) => {
        setLocalFactors(prev => ({ ...prev, [key]: value }));
    };

    const fFactor = parseFloat(localFactors.founder.replace(',', '.')) || 0;
    const cFactor = parseFloat(localFactors.coach.replace(',', '.')) || 0;

    const totalShares = (foundersList.length * fFactor) + (coachesList.length * cFactor);
    const founderPct = totalShares > 0 ? (fFactor / totalShares) : 0;
    const coachPct = totalShares > 0 ? (cFactor / totalShares) : 0;

    const applyFactors = () => {
        captureState();
        updateSettings('shareFactors' as any, { founder: fFactor, coach: cFactor });
    };

    React.useEffect(() => {
        if (totalShares === 0) return;
        
        const newSplits: Record<string, number> = {};
        foundersList.forEach(f => newSplits[f.name] = founderPct);
        coachesList.forEach(c => newSplits[c.name] = coachPct);
        
        const newFix: Record<string, number> = {};
        team.forEach(m => {
            const fix = parseFloat((m as any).fixedSalary) || 0;
            if (fix > 0) {
                newFix[m.name] = fix;
            }
        });

        const currentSplitsStr = JSON.stringify((settings as any)?.splits || {});
        const currentFixStr = JSON.stringify((settings as any)?.fixedSalaries || {});
        const newSplitsStr = JSON.stringify(newSplits);
        const newFixStr = JSON.stringify(newFix);

        if (currentSplitsStr !== newSplitsStr) {
            updateSettings('splits' as any, newSplits);
        }
        
        if (currentFixStr !== newFixStr) {
            updateSettings('fixedSalaries' as any, newFix);
        }

    }, [team, founderPct, coachPct, totalShares, updateSettings, foundersList, coachesList, settings]);

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => e.target.select();

    return (
        <div className="space-y-6">
            <p className="text-xs text-gray-400 leading-relaxed bg-slate-900/50 p-4 rounded border border-slate-700">
                Die interne Geldfluss-Matrix nutzt das bewährte <strong className="text-white">Faktor-Modell</strong>. 
                Trage unten deine Wunschfaktoren ein (z.B. 35 für Gründer, 15 für Coaches). 
                Das System multipliziert diese Faktoren mit den Köpfen im Team und errechnet daraus den finalen prozentualen Gewinnanteil. 
                (Abrechnungs-Faktoren für den Mandanten und Fix-Gehälter pflegst du direkt in der Team-Verwaltung).
            </p>
            
            <div className="bg-[#0c0d11] p-6 rounded-xl border border-slate-700 shadow-xl relative overflow-hidden">
                <div className="absolute top-0 left-0 w-1 h-full bg-[#E6C299]"></div>
                
                <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-6 border-b border-slate-800 pb-6 gap-4">
                    <div className="flex gap-4 w-full md:w-auto">
                        <div className="bg-slate-900 border border-slate-700 p-3 rounded flex flex-col gap-1 w-1/2 md:w-auto">
                            <span className="text-[10px] uppercase text-slate-400 font-bold tracking-widest">Faktor Gründer</span>
                            <input 
                                type="text" 
                                value={localFactors.founder} 
                                onChange={(e) => handleFactorChange('founder', e.target.value)}
                                onBlur={applyFactors}
                                onFocus={handleFocus}
                                className="bg-transparent text-[#E6C299] font-mono font-black text-xl outline-none"
                            />
                        </div>
                        <div className="bg-slate-900 border border-slate-700 p-3 rounded flex flex-col gap-1 w-1/2 md:w-auto">
                            <span className="text-[10px] uppercase text-slate-400 font-bold tracking-widest">Faktor Coach</span>
                            <input 
                                type="text" 
                                value={localFactors.coach} 
                                onChange={(e) => handleFactorChange('coach', e.target.value)}
                                onBlur={applyFactors}
                                onFocus={handleFocus}
                                className="bg-transparent text-white font-mono font-black text-xl outline-none"
                            />
                        </div>
                    </div>
                    <div className="text-right">
                        <h4 className="text-white font-black uppercase tracking-widest text-base mb-1">Live-Kalkulator</h4>
                        <p className="text-xs text-slate-500 font-bold">Summe interner Faktoren: <strong className="text-[#E6C299] text-sm">{totalShares}</strong></p>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div>
                        <h5 className="text-[10px] uppercase text-slate-500 font-bold mb-3 flex justify-between">
                            <span>Gründer (Faktor {fFactor} pro Kopf)</span>
                            <span className="text-[#E6C299]">{(founderPct * 100).toFixed(1)}% ECHTER ANTEIL</span>
                        </h5>
                        <div className="space-y-2">
                            {foundersList.map(f => (
                                <div key={f.id} className="bg-slate-800/40 p-3 rounded border border-slate-700/50 flex justify-between items-center">
                                    <span className="text-white font-bold text-sm">{f.name}</span>
                                    <span className="text-[#E6C299] font-mono font-bold">{(founderPct * 100).toFixed(1)}%</span>
                                </div>
                            ))}
                            {foundersList.length === 0 && <span className="text-xs text-slate-600 italic">Keine Gründer erkannt.</span>}
                        </div>
                    </div>

                    <div>
                        <h5 className="text-[10px] uppercase text-slate-500 font-bold mb-3 flex justify-between">
                            <span>Coaches (Faktor {cFactor} pro Kopf)</span>
                            <span className="text-slate-300">{(coachPct * 100).toFixed(1)}% ECHTER ANTEIL</span>
                        </h5>
                        <div className="space-y-2">
                            {coachesList.map(c => (
                                <div key={c.id} className="bg-slate-800/20 p-3 rounded border border-slate-800 flex justify-between items-center">
                                    <span className="text-slate-300 font-bold text-sm">{c.name}</span>
                                    <span className="text-white font-mono font-bold">{(coachPct * 100).toFixed(1)}%</span>
                                </div>
                            ))}
                            {coachesList.length === 0 && <span className="text-xs text-slate-600 italic">Keine aktiven Coaches.</span>}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

interface TutorialCategory {
    name: string;
}

interface VideoTutorial {
    id: string;
    title: string;
    description: string;
    src: string;
    category?: string;
    /** Optionales Vorschaubild-URL (Firebase Storage / CDN) */
    thumbnailUrl?: string;
    /** Academy-Sichtbarkeit; Legacy ohne Feld = öffentlich */
    isPublic?: boolean;
}

const isAcademyVideoPublic = (video: Pick<VideoTutorial, 'isPublic'>) => video.isPublic !== false;

const VideoManagement = () => {
    const { settings, updateSettings, captureState } = useAppStore(state => ({
        settings: state.settings,
        updateSettings: state.updateSettings,
        captureState: state.captureState
    }));

    const videos: VideoTutorial[] = (settings as any)?.tutorialVideos || [];
    
    // Bereinigung von Legacy-Datenstrukturen (accessLevel entfernen)
    const categories: TutorialCategory[] = ((settings as any)?.tutorialCategories || []).map((c: any) => {
        if (typeof c === 'string') return { name: c }; 
        return { name: c.name };
    });
    
    if (categories.length === 0) categories.push({ name: 'Allgemein' });

    const [newCategoryName, setNewCategoryName] = useState('');
    const [editingCatIndex, setEditingCatIndex] = useState<number | null>(null);
    const [editCatDraft, setEditCatDraft] = useState<TutorialCategory | null>(null);

    const [editingVideo, setEditingVideo] = useState<Partial<VideoTutorial> | null>(null);
    const [pendingVideoThumb, setPendingVideoThumb] = useState<File | null>(null);
    const [savingVideo, setSavingVideo] = useState(false);

    const handleMoveCategory = (index: number, direction: 'up' | 'down') => {
        if (direction === 'up' && index === 0) return;
        if (direction === 'down' && index === categories.length - 1) return;
        
        captureState();
        const newCats = [...categories];
        const swapIndex = direction === 'up' ? index - 1 : index + 1;
        [newCats[index], newCats[swapIndex]] = [newCats[swapIndex], newCats[index]];
        
        updateSettings('tutorialCategories' as any, newCats);
    };

    const handleAddCategory = () => {
        if (!newCategoryName.trim()) return;
        const cleanName = newCategoryName.trim();
        if (categories.some(c => c.name.toLowerCase() === cleanName.toLowerCase())) return; 
        
        captureState();
        updateSettings('tutorialCategories' as any, [...categories, { name: cleanName }]);
        setNewCategoryName('');
    };

    const handleSaveCategoryEdit = () => {
        if (editingCatIndex === null || !editCatDraft || !editCatDraft.name.trim()) return;
        const cleanName = editCatDraft.name.trim();
        const oldName = categories[editingCatIndex].name;
        
        captureState();
        
        const newCats = [...categories];
        newCats[editingCatIndex] = { name: cleanName };
        
        if (oldName !== cleanName) {
            const updatedVideos = videos.map(v => v.category === oldName ? { ...v, category: cleanName } : v);
            updateSettings('tutorialVideos' as any, updatedVideos);
        }

        updateSettings('tutorialCategories' as any, newCats);
        setEditingCatIndex(null);
        setEditCatDraft(null);
    };

    const handleDeleteCategory = (catName: string) => {
        const isAssigned = videos.some(v => v.category === catName);

        if (isAssigned) {
            alert(`❌ Löschen blockiert: Die Rubrik "${catName}" ist noch an mindestens ein Video zugewiesen. Weise diese Videos zuerst einer anderen Rubrik zu oder lösche sie.`);
            return;
        }

        if (categories.length <= 1) {
            alert(`❌ Löschen blockiert: Du musst mindestens eine Basis-Rubrik im System behalten.`);
            return;
        }

        if(window.confirm(`Rubrik "${catName}" wirklich löschen?`)) {
            captureState();
            updateSettings('tutorialCategories' as any, categories.filter(c => c.name !== catName));
        }
    };

    const handleSaveVideo = async () => {
        if (!editingVideo || !editingVideo.title) return;

        setSavingVideo(true);
        try {
            let thumbnailUrl = (editingVideo.thumbnailUrl || '').trim();
            if (pendingVideoThumb) {
                const safeName = pendingVideoThumb.name.replace(/[^\w.-]+/g, '_');
                const storagePath = `academy/video-thumbs/${Date.now()}_${safeName}`;
                const thumbRef = ref(storage, storagePath);
                await uploadBytes(thumbRef, pendingVideoThumb);
                thumbnailUrl = await getDownloadURL(thumbRef);
            }

            captureState();

            let newVideos = [...videos];
            if (editingVideo.id) {
                newVideos = newVideos.map((v) =>
                    v.id === editingVideo.id
                        ? ({
                              ...v,
                              ...editingVideo,
                              thumbnailUrl: thumbnailUrl || undefined,
                              isPublic: editingVideo.isPublic === true,
                          } as VideoTutorial)
                        : v
                );
            } else {
                newVideos.push({
                    id: Date.now().toString(),
                    title: editingVideo.title || '',
                    description: editingVideo.description || '',
                    src: editingVideo.src || '',
                    category: editingVideo.category || categories[0].name,
                    ...(thumbnailUrl ? { thumbnailUrl } : {}),
                    isPublic: editingVideo.isPublic === true,
                });
            }

            updateSettings('tutorialVideos' as any, newVideos);
            setEditingVideo(null);
            setPendingVideoThumb(null);
        } catch (err) {
            console.error('[VideoManagement] Speichern fehlgeschlagen', err);
            window.alert('Video konnte nicht gespeichert werden (Vorschaubild-Upload?).');
        } finally {
            setSavingVideo(false);
        }
    };

    const handleDeleteVideo = (id: string) => {
        if(window.confirm("Video wirklich löschen?")) {
            captureState(); 
            const newVideos = videos.filter(v => v.id !== id);
            updateSettings('tutorialVideos' as any, newVideos);
        }
    };

    const handleMoveVideo = (id: string, direction: 'up' | 'down') => {
        const globalIndex = videos.findIndex((v) => v.id === id);
        if (globalIndex < 0) return;
        const category = videos[globalIndex].category || categories[0]?.name || 'Allgemein';
        const siblingIndexes = videos
            .map((v, i) => ((v.category || categories[0]?.name || 'Allgemein') === category ? i : -1))
            .filter((i) => i >= 0);
        const posInCat = siblingIndexes.indexOf(globalIndex);
        if (posInCat < 0) return;
        const swapPos = direction === 'up' ? posInCat - 1 : posInCat + 1;
        if (swapPos < 0 || swapPos >= siblingIndexes.length) return;

        captureState();
        const newVideos = [...videos];
        const a = siblingIndexes[posInCat];
        const b = siblingIndexes[swapPos];
        [newVideos[a], newVideos[b]] = [newVideos[b], newVideos[a]];
        updateSettings('tutorialVideos' as any, newVideos);
    };

    /** Flache Liste nach Rubrik-Reihenfolge neu aufbauen (innerhalb der Rubrik bleibt die Reihenfolge). */
    const handleSortVideosByCategory = () => {
        if (!videos.length) return;
        captureState();
        const catNames = categories.map((c) => c.name);
        const used = new Set<string>();
        const sorted: VideoTutorial[] = [];
        catNames.forEach((name) => {
            videos.forEach((v) => {
                if ((v.category || catNames[0]) === name && !used.has(v.id)) {
                    sorted.push(v);
                    used.add(v.id);
                }
            });
        });
        videos.forEach((v) => {
            if (!used.has(v.id)) sorted.push(v);
        });
        updateSettings('tutorialVideos' as any, sorted);
    };

    const handleToggleVideoPublic = (id: string) => {
        captureState();
        const newVideos = videos.map((v) =>
            v.id === id ? { ...v, isPublic: !isAcademyVideoPublic(v) } : v
        );
        updateSettings('tutorialVideos' as any, newVideos);
    };

    const handleAddNewVideo = () => {
        setPendingVideoThumb(null);
        setEditingVideo({
            title: '',
            description: '',
            src: '',
            thumbnailUrl: '',
            category: categories.length > 0 ? categories[0].name : 'Allgemein',
            isPublic: false,
        });
    };

    const videosGrouped = (() => {
        const byCat: { name: string; items: { video: VideoTutorial; globalIndex: number }[] }[] = [];
        const seen = new Set<string>();
        categories.forEach((c) => {
            const items = videos
                .map((video, globalIndex) => ({ video, globalIndex }))
                .filter(({ video }) => (video.category || categories[0]?.name) === c.name);
            items.forEach(({ video }) => seen.add(video.id));
            byCat.push({ name: c.name, items });
        });
        const orphans = videos
            .map((video, globalIndex) => ({ video, globalIndex }))
            .filter(({ video }) => !seen.has(video.id));
        if (orphans.length) byCat.push({ name: 'Sonstige & Ohne Rubrik', items: orphans });
        return byCat;
    })();

    return (
        <>
            <div className="bg-[#0a0a0c] border border-[#222] p-4 rounded-lg mb-8 shadow-inner">
                <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 border-b border-[#222] pb-2">1. Rubriken verwalten</h4>
                
                <div className="flex flex-col gap-2 mb-6">
                    {categories.map((cat, idx) => (
                        <div key={idx} className="flex items-center justify-between bg-black p-3 rounded-lg border border-slate-800 shadow-md">
                            {editingCatIndex === idx ? (
                                <div className="flex items-center gap-3 w-full">
                                    <input 
                                        type="text" 
                                        value={editCatDraft?.name || ''} 
                                        onChange={(e) => setEditCatDraft(prev => prev ? {...prev, name: e.target.value} : null)}
                                        className="bg-[#0a0a0c] border border-slate-700 text-white text-xs font-bold px-3 py-2 rounded focus:border-[#E6C299] outline-none flex-1"
                                    />
                                    <button onClick={handleSaveCategoryEdit} className="bg-emerald-600 hover:bg-emerald-500 text-white p-2 rounded transition-colors"><Save size={14}/></button>
                                    <button onClick={() => setEditingCatIndex(null)} className="bg-slate-700 hover:bg-slate-600 text-white p-2 rounded transition-colors"><X size={14}/></button>
                                </div>
                            ) : (
                                <>
                                    <div className="flex items-center gap-4">
                                        <div className="flex flex-col gap-0 border-r border-slate-800 pr-3">
                                            <button onClick={() => handleMoveCategory(idx, 'up')} disabled={idx === 0} className="text-slate-600 hover:text-white disabled:opacity-30 focus:outline-none transition-colors"><ArrowUp size={14}/></button>
                                            <button onClick={() => handleMoveCategory(idx, 'down')} disabled={idx === categories.length - 1} className="text-slate-600 hover:text-white disabled:opacity-30 focus:outline-none transition-colors"><ArrowDown size={14}/></button>
                                        </div>
                                        <div className="font-bold text-[#E6C299] text-sm tracking-wide">{cat.name}</div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <button onClick={() => { setEditingCatIndex(idx); setEditCatDraft(cat); }} className="text-slate-500 hover:text-[#E6C299] p-1 transition-colors"><Edit2 size={14}/></button>
                                        <button onClick={() => handleDeleteCategory(cat.name)} className="text-slate-500 hover:text-red-500 p-1 transition-colors"><Trash2 size={14}/></button>
                                    </div>
                                </>
                            )}
                        </div>
                    ))}
                </div>

                <div className="flex items-center gap-2 pt-2 border-t border-[#222]">
                    <input 
                        type="text" 
                        value={newCategoryName} 
                        onChange={(e) => setNewCategoryName(e.target.value)} 
                        onKeyDown={(e) => e.key === 'Enter' && handleAddCategory()}
                        placeholder="Neue Rubrik anlegen..." 
                        className="bg-black border border-slate-700 text-white text-xs px-3 py-2 rounded focus:border-[#E6C299] outline-none transition-colors w-64"
                    />
                    <button 
                        onClick={handleAddCategory}
                        disabled={!newCategoryName.trim()}
                        className="bg-slate-800 hover:bg-slate-700 text-white px-3 py-2 rounded border border-slate-700 transition-colors disabled:opacity-50 flex items-center gap-1 text-xs font-bold uppercase tracking-wider"
                    >
                        <Plus size={14} /> Hinzufügen
                    </button>
                </div>
            </div>

            <div className="flex flex-wrap justify-between items-center gap-3 mb-4 mt-8 border-t border-[#222] pt-6">
                <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest">2. Videos eintragen</h4>
                <div className="flex flex-wrap items-center gap-2 ml-auto">
                    {videos.length > 0 && (
                        <button
                            type="button"
                            onClick={handleSortVideosByCategory}
                            className="bg-slate-800 hover:bg-slate-700 text-[#E6C299] px-3 py-2 rounded border border-slate-700 transition-colors flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wider"
                            title="Videos in der Speicher-Reihenfolge nach Rubriken gruppieren"
                        >
                            <ListTree size={14} /> Nach Rubrik sortieren
                        </button>
                    )}
                    {!editingVideo && (
                        <UI.Button onClick={handleAddNewVideo}>+ Video hinzufügen</UI.Button>
                    )}
                </div>
            </div>

            {editingVideo && (
                <div className="bg-black p-6 rounded-lg border border-slate-700 mt-4 space-y-4 mb-6 shadow-2xl relative">
                    <div className="absolute top-0 left-0 w-1 h-full bg-[#E6C299] rounded-l-lg"></div>
                    <h4 className="text-sm font-bold text-[#E6C299] uppercase tracking-wider mb-4 border-b border-gray-800 pb-2">
                        {editingVideo.id ? 'Video bearbeiten' : 'Neues Video'}
                    </h4>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="md:col-span-2">
                            <span className="text-[10px] text-gray-400 uppercase font-bold mb-1 block">Titel *</span>
                            <UI.Input value={editingVideo.title || ''} onChange={(e) => setEditingVideo({...editingVideo, title: e.target.value})} placeholder="Titel des Tutorials" />
                        </div>
                        
                        <div className="md:col-span-2 bg-[#050505] p-4 border border-slate-800 rounded-lg">
                            <span className="text-[10px] text-gray-400 uppercase font-bold mb-1 block">Zugeordnete Rubrik *</span>
                            <select 
                                value={editingVideo.category || (categories.length > 0 ? categories[0].name : '')} 
                                onChange={(e) => setEditingVideo({...editingVideo, category: e.target.value})}
                                className="w-full bg-[#0a0a0c] border border-slate-700 text-white text-sm font-bold rounded-lg px-4 py-3 appearance-none cursor-pointer focus:outline-none focus:border-[#E6C299] transition-all shadow-inner"
                            >
                                {categories.map(cat => (
                                    <option key={cat.name} value={cat.name}>{cat.name}</option>
                                ))}
                            </select>
                        </div>

                        <div className="md:col-span-2">
                            <span className="text-[10px] text-gray-400 uppercase font-bold mb-1 block">Beschreibung</span>
                            <textarea
                                value={editingVideo.description || ''}
                                onChange={(e) => setEditingVideo({ ...editingVideo, description: e.target.value })}
                                placeholder={"Kurze Beschreibung.\nLinks mit https://… einfügen — werden in der Academy klickbar."}
                                rows={4}
                                className="w-full bg-[#0a0a0c] border border-slate-700 text-white text-sm rounded-lg px-4 py-3 outline-none focus:border-[#E6C299] transition-all shadow-inner resize-y min-h-[96px]"
                            />
                            <p className="text-[10px] text-gray-500 mt-1.5 leading-relaxed">
                                Partner-/Produktlinks als volle URL eintragen (z.&nbsp;B. <span className="font-mono text-slate-400">https://example.com/…</span>) — in der Academy werden sie automatisch verlinkt.
                            </p>
                            {(editingVideo.description || '').trim() ? (
                                <div className="mt-3 rounded-lg border border-slate-800 bg-[#050505] p-3">
                                    <span className="text-[9px] text-slate-500 uppercase font-black tracking-widest block mb-2">Vorschau</span>
                                    <LinkifiedText
                                        text={editingVideo.description}
                                        className="text-sm text-slate-300 leading-relaxed"
                                    />
                                </div>
                            ) : null}
                        </div>
                        <div className="md:col-span-2">
                            <span className="text-[10px] text-gray-400 uppercase font-bold mb-1 block">Embed Link</span>
                            <UI.Input value={editingVideo.src || ''} onChange={(e) => setEditingVideo({...editingVideo, src: e.target.value})} placeholder="z.B. https://vimeo.com/123456789/abcdef oder YouTube-Embed" />
                            <p className="text-[10px] text-gray-500 mt-1 italic">
                                Vimeo: kompletten Share-Link mit Privacy-Hash einfügen (`https://vimeo.com/ID/hash…`). Ohne Hash kein Player. Vorschaubild kommt aus dem Player — optional eigenes Bild unten.
                            </p>
                        </div>
                        <div className="md:col-span-2 space-y-2">
                            <span className="text-[10px] text-gray-400 uppercase font-bold mb-1 block">Vorschaubild (optional)</span>
                            <UI.Input
                                value={editingVideo.thumbnailUrl || ''}
                                onChange={(e) =>
                                    setEditingVideo({ ...editingVideo, thumbnailUrl: e.target.value })
                                }
                                placeholder="https://… oder Datei hochladen"
                            />
                            <div className="flex flex-wrap items-center gap-3">
                                <label className="inline-flex items-center gap-2 cursor-pointer text-xs text-slate-300 border border-slate-700 rounded-lg px-3 py-2 hover:border-[#E6C299]/60">
                                    <Upload className="w-3.5 h-3.5 text-[#E6C299]" />
                                    <span>{pendingVideoThumb ? pendingVideoThumb.name : 'Bild hochladen'}</span>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        className="hidden"
                                        onChange={(e) =>
                                            setPendingVideoThumb(e.target.files?.[0] || null)
                                        }
                                    />
                                </label>
                                {(editingVideo.thumbnailUrl || pendingVideoThumb) && (
                                    <img
                                        src={
                                            pendingVideoThumb
                                                ? URL.createObjectURL(pendingVideoThumb)
                                                : editingVideo.thumbnailUrl || ''
                                        }
                                        alt=""
                                        className="h-12 w-20 object-cover rounded border border-slate-700"
                                    />
                                )}
                                {editingVideo.thumbnailUrl && !pendingVideoThumb && (
                                    <button
                                        type="button"
                                        className="text-[10px] uppercase font-bold text-red-400"
                                        onClick={() =>
                                            setEditingVideo({ ...editingVideo, thumbnailUrl: '' })
                                        }
                                    >
                                        Entfernen
                                    </button>
                                )}
                            </div>
                        </div>
                        <label className="md:col-span-2 flex items-start gap-3 cursor-pointer rounded-lg border border-slate-700 bg-[#050505] p-4">
                            <input
                                type="checkbox"
                                checked={editingVideo.isPublic === true}
                                onChange={(e) => setEditingVideo({ ...editingVideo, isPublic: e.target.checked })}
                                className="accent-[#E6C299] w-4 h-4 mt-0.5 shrink-0"
                            />
                            <span className="text-sm text-slate-300 leading-snug">
                                <span className="font-bold text-[#E6C299]">In Academy öffentlich</span>
                                <span className="block text-xs text-slate-500 mt-1">
                                    Aus = nur hier in den globalen Daten sichtbar. An = Mandanten sehen das Video unter Academy.
                                </span>
                            </span>
                        </label>
                    </div>
                    <div className="flex justify-end gap-3 pt-4 mt-2 border-t border-gray-800">
                        <UI.Button
                            variant="secondary"
                            onClick={() => {
                                setEditingVideo(null);
                                setPendingVideoThumb(null);
                            }}
                        >
                            Abbrechen
                        </UI.Button>
                        <UI.Button
                            variant="primary"
                            onClick={() => void handleSaveVideo()}
                            disabled={!editingVideo.title || !editingVideo.category || savingVideo}
                        >
                            {savingVideo ? 'Speichern…' : 'Speichern'}
                        </UI.Button>
                    </div>
                </div>
            )}

            <div className="space-y-6">
                {videos.length === 0 && !editingVideo && <p className="text-gray-600 text-center py-4 text-sm italic">Keine Videos vorhanden.</p>}
                {videosGrouped.map((group) => {
                    if (!group.items.length && group.name !== 'Sonstige & Ohne Rubrik') {
                        return (
                            <div key={group.name} className="rounded-lg border border-dashed border-slate-800 px-4 py-3">
                                <h5 className="text-[10px] font-black uppercase tracking-widest text-slate-600">
                                    {group.name} <span className="normal-case font-semibold tracking-normal">· leer</span>
                                </h5>
                            </div>
                        );
                    }
                    if (!group.items.length) return null;
                    return (
                        <div key={group.name} className="rounded-xl border border-slate-800 bg-[#0a0a0c]/80 overflow-hidden">
                            <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-slate-800 bg-black/40">
                                <h5 className="text-[10px] font-black uppercase tracking-widest text-[#E6C299]">
                                    {group.name}
                                </h5>
                                <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">
                                    {group.items.length} Video{group.items.length === 1 ? '' : 's'}
                                </span>
                            </div>
                            <div className="space-y-2 p-3">
                                {group.items.map(({ video }, idxInCat) => {
                                    const publiclyOn = isAcademyVideoPublic(video);
                                    return (
                                        <div
                                            key={video.id}
                                            className={`bg-gray-800/50 p-3 rounded border flex items-center justify-between flex-wrap gap-2 hover:bg-gray-800 transition-colors ${
                                                publiclyOn ? 'border-gray-700' : 'border-amber-500/40'
                                            }`}
                                        >
                                            <div>
                                                <div className="flex items-center gap-3 flex-wrap">
                                                    <p className="font-bold text-white text-sm">{video.title}</p>
                                                    <span
                                                        className={`text-[9px] px-2 py-0.5 rounded font-black uppercase tracking-widest border ${
                                                            publiclyOn
                                                                ? 'bg-emerald-900/30 text-emerald-400 border-emerald-800/50'
                                                                : 'bg-amber-900/30 text-amber-300 border-amber-700/40'
                                                        }`}
                                                    >
                                                        {publiclyOn ? 'Öffentlich' : 'Aus / Entwurf'}
                                                    </span>
                                                </div>
                                                <p className="text-xs text-gray-500 truncate max-w-md font-mono mt-1">{video.src}</p>
                                            </div>
                                            <div className="flex gap-2 items-center">
                                                <div className="flex flex-col leading-none">
                                                    <button
                                                        type="button"
                                                        onClick={() => handleMoveVideo(video.id, 'up')}
                                                        disabled={idxInCat === 0}
                                                        className="text-slate-500 hover:text-[#E6C299] disabled:opacity-30 disabled:hover:text-slate-500 p-1 transition-colors"
                                                        title="In dieser Rubrik nach oben"
                                                    >
                                                        <ArrowUp size={14} />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleMoveVideo(video.id, 'down')}
                                                        disabled={idxInCat === group.items.length - 1}
                                                        className="text-slate-500 hover:text-[#E6C299] disabled:opacity-30 disabled:hover:text-slate-500 p-1 transition-colors"
                                                        title="In dieser Rubrik nach unten"
                                                    >
                                                        <ArrowDown size={14} />
                                                    </button>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => handleToggleVideoPublic(video.id)}
                                                    title={publiclyOn ? 'In Academy ausblenden' : 'In Academy freischalten'}
                                                    className={`px-2 py-1 rounded border text-[9px] font-black uppercase tracking-widest flex items-center gap-1 ${
                                                        publiclyOn
                                                            ? 'bg-emerald-900/30 text-emerald-400 border-emerald-800/50 hover:bg-emerald-600 hover:text-white'
                                                            : 'bg-amber-900/30 text-amber-300 border-amber-700/40 hover:bg-amber-600 hover:text-black'
                                                    }`}
                                                >
                                                    {publiclyOn ? <Eye size={12} /> : <EyeOff size={12} />}
                                                    {publiclyOn ? 'An' : 'Aus'}
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        setPendingVideoThumb(null);
                                                        setEditingVideo({
                                                            ...video,
                                                            isPublic: isAcademyVideoPublic(video),
                                                        });
                                                    }}
                                                    className="text-gray-400 hover:text-[#E6C299] text-xs uppercase font-bold px-2 py-1"
                                                >
                                                    Bearbeiten
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteVideo(video.id)}
                                                    className="text-gray-600 hover:text-red-500 text-xs uppercase font-bold px-2 py-1"
                                                >
                                                    Löschen
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
            </div>
        </>
    );
};

const detectFileType = (fileName: string): DownloadFileType => {
    const ext = fileName.split('.').pop()?.toLowerCase() || '';
    if (ext === 'pdf') return 'pdf';
    if (ext === 'zip' || ext === 'rar' || ext === '7z') return 'zip';
    if (ext === 'xlsx' || ext === 'xls' || ext === 'csv') return 'xlsx';
    if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(ext)) return 'image';
    return 'other';
};

const safeStorageFileName = (name: string) =>
    name.replace(/[^a-zA-Z0-9._-]/g, '_');

const DownloadManagement = () => {
    const { settings, updateSettings, captureState } = useAppStore((state) => ({
        settings: state.settings,
        updateSettings: state.updateSettings,
        captureState: state.captureState,
    }));

    const downloads: DownloadItem[] = Array.isArray(settings?.downloadItems)
        ? (settings.downloadItems as DownloadItem[])
        : [];

    const categories: { name: string }[] = (Array.isArray(settings?.downloadCategories)
        ? settings.downloadCategories
        : []
    ).map((c: any) => {
        if (typeof c === 'string') return { name: c };
        return { name: c.name };
    });
    if (categories.length === 0) categories.push({ name: 'Allgemein' });

    const [newCategoryName, setNewCategoryName] = useState('');
    const [editingCatIndex, setEditingCatIndex] = useState<number | null>(null);
    const [editCatDraft, setEditCatDraft] = useState<{ name: string } | null>(null);

    const [editing, setEditing] = useState<Partial<DownloadItem> | null>(null);
    const [pendingFile, setPendingFile] = useState<File | null>(null);
    const [pendingThumb, setPendingThumb] = useState<File | null>(null);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);

    const handleMoveCategory = (index: number, direction: 'up' | 'down') => {
        if (direction === 'up' && index === 0) return;
        if (direction === 'down' && index === categories.length - 1) return;
        captureState();
        const newCats = [...categories];
        const swapIndex = direction === 'up' ? index - 1 : index + 1;
        [newCats[index], newCats[swapIndex]] = [newCats[swapIndex], newCats[index]];
        updateSettings('downloadCategories', newCats);
    };

    const handleAddCategory = () => {
        if (!newCategoryName.trim()) return;
        const cleanName = newCategoryName.trim();
        if (categories.some((c) => c.name.toLowerCase() === cleanName.toLowerCase())) return;
        captureState();
        updateSettings('downloadCategories', [...categories, { name: cleanName }]);
        setNewCategoryName('');
    };

    const handleSaveCategoryEdit = () => {
        if (editingCatIndex === null || !editCatDraft || !editCatDraft.name.trim()) return;
        const cleanName = editCatDraft.name.trim();
        const oldName = categories[editingCatIndex].name;
        captureState();
        const newCats = [...categories];
        newCats[editingCatIndex] = { name: cleanName };
        if (oldName !== cleanName) {
            const updatedDownloads = downloads.map((d) =>
                d.category === oldName ? { ...d, category: cleanName } : d
            );
            updateSettings('downloadItems', updatedDownloads);
        }
        updateSettings('downloadCategories', newCats);
        setEditingCatIndex(null);
        setEditCatDraft(null);
    };

    const handleDeleteCategory = (catName: string) => {
        const isAssigned = downloads.some((d) => d.category === catName);
        if (isAssigned) {
            alert(
                `❌ Löschen blockiert: Die Rubrik "${catName}" ist noch an mindestens einen Download zugewiesen.`
            );
            return;
        }
        if (categories.length <= 1) {
            alert('❌ Löschen blockiert: Mindestens eine Download-Rubrik muss bleiben.');
            return;
        }
        if (window.confirm(`Download-Rubrik "${catName}" wirklich löschen?`)) {
            captureState();
            updateSettings(
                'downloadCategories',
                categories.filter((c) => c.name !== catName)
            );
        }
    };

    const handleAddNew = () => {
        setPendingFile(null);
        setPendingThumb(null);
        setUploadError(null);
        setEditing({
            title: '',
            description: '',
            fileUrl: '',
            fileType: 'pdf',
            category: categories[0]?.name || 'Allgemein',
            fileName: '',
            thumbnailUrl: '',
        });
    };

    const handleFilePick = (file: File | null) => {
        setPendingFile(file);
        setUploadError(null);
        if (!file || !editing) return;
        setEditing({
            ...editing,
            fileName: file.name,
            fileType: detectFileType(file.name),
            title: editing.title || file.name.replace(/\.[^.]+$/, ''),
        });
    };

    const handleThumbPick = (file: File | null) => {
        setPendingThumb(file);
        setUploadError(null);
    };

    const handleSave = async () => {
        if (!editing || !editing.title?.trim()) return;
        setUploadError(null);
        setIsUploading(true);
        captureState();

        try {
            let fileUrl = editing.fileUrl || '';
            let fileName = editing.fileName || '';
            let fileType = editing.fileType || 'other';
            let thumbnailUrl = editing.thumbnailUrl || '';

            if (pendingFile) {
                const safeName = safeStorageFileName(pendingFile.name);
                const storagePath = `academy_downloads/${Date.now()}_${safeName}`;
                const fileRef = ref(storage, storagePath);
                await uploadBytes(fileRef, pendingFile);
                fileUrl = await getDownloadURL(fileRef);
                fileName = pendingFile.name;
                fileType = detectFileType(pendingFile.name);
            }

            if (pendingThumb) {
                const safeName = safeStorageFileName(pendingThumb.name);
                const storagePath = `academy_downloads/thumbs/${Date.now()}_${safeName}`;
                const thumbRef = ref(storage, storagePath);
                await uploadBytes(thumbRef, pendingThumb);
                thumbnailUrl = await getDownloadURL(thumbRef);
            } else if (fileType === 'image' && fileUrl && !thumbnailUrl) {
                thumbnailUrl = fileUrl;
            }

            if (!fileUrl) {
                setUploadError('Bitte eine Datei hochladen oder eine bestehende Datei behalten.');
                setIsUploading(false);
                return;
            }

            const payload: DownloadItem = {
                id: editing.id || Date.now().toString(),
                title: editing.title.trim(),
                description: (editing.description || '').trim(),
                fileUrl,
                fileType,
                category: editing.category || categories[0]?.name || 'Allgemein',
                uploadedAt: editing.uploadedAt || new Date().toISOString(),
                ...(fileName ? { fileName } : {}),
                ...(thumbnailUrl ? { thumbnailUrl } : {}),
            };

            let next = [...downloads];
            if (editing.id) {
                next = next.map((d) => (d.id === editing.id ? payload : d));
            } else {
                next.push(payload);
            }

            updateSettings('downloadItems', next);
            setEditing(null);
            setPendingFile(null);
            setPendingThumb(null);
        } catch (err: any) {
            console.error('[DownloadManagement] Upload/Save failed:', err);
            setUploadError(err?.message || 'Upload oder Speichern fehlgeschlagen.');
        } finally {
            setIsUploading(false);
        }
    };

    const handleDelete = async (item: DownloadItem) => {
        if (!window.confirm(`Download „${item.title}“ wirklich löschen?`)) return;
        captureState();
        updateSettings(
            'downloadItems',
            downloads.filter((d) => d.id !== item.id)
        );
        // Best effort: Storage-Datei + Vorschaubild entfernen
        try {
            const urls = [item.fileUrl, item.thumbnailUrl].filter(
                (u, i, arr) => !!u && u.includes('firebasestorage') && arr.indexOf(u) === i
            );
            for (const url of urls) {
                const match = url!.match(/\/o\/([^?]+)/);
                if (match?.[1]) {
                    await deleteObject(ref(storage, decodeURIComponent(match[1])));
                }
            }
        } catch (e) {
            console.warn('[DownloadManagement] Storage-Löschen optional fehlgeschlagen:', e);
        }
    };

    const handleMove = (index: number, direction: 'up' | 'down') => {
        if (direction === 'up' && index === 0) return;
        if (direction === 'down' && index === downloads.length - 1) return;
        captureState();
        const next = [...downloads];
        const swap = direction === 'up' ? index - 1 : index + 1;
        [next[index], next[swap]] = [next[swap], next[index]];
        updateSettings('downloadItems', next);
    };

    return (
        <>
            <div className="bg-[#0a0a0c] border border-[#222] p-4 rounded-lg mb-8 shadow-inner">
                <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 border-b border-[#222] pb-2">
                    1. Download-Rubriken verwalten
                </h4>
                <div className="flex flex-col gap-2 mb-6">
                    {categories.map((cat, idx) => (
                        <div
                            key={idx}
                            className="flex items-center justify-between bg-black p-3 rounded-lg border border-slate-800 shadow-md"
                        >
                            {editingCatIndex === idx ? (
                                <div className="flex items-center gap-3 w-full">
                                    <input
                                        type="text"
                                        value={editCatDraft?.name || ''}
                                        onChange={(e) =>
                                            setEditCatDraft((prev) =>
                                                prev ? { ...prev, name: e.target.value } : null
                                            )
                                        }
                                        className="bg-[#0a0a0c] border border-slate-700 text-white text-xs font-bold px-3 py-2 rounded focus:border-[#E6C299] outline-none flex-1"
                                    />
                                    <button
                                        onClick={handleSaveCategoryEdit}
                                        className="bg-emerald-600 hover:bg-emerald-500 text-white p-2 rounded"
                                    >
                                        <Save size={14} />
                                    </button>
                                    <button
                                        onClick={() => setEditingCatIndex(null)}
                                        className="bg-slate-700 hover:bg-slate-600 text-white p-2 rounded"
                                    >
                                        <X size={14} />
                                    </button>
                                </div>
                            ) : (
                                <>
                                    <div className="flex items-center gap-4">
                                        <div className="flex flex-col gap-0 border-r border-slate-800 pr-3">
                                            <button
                                                onClick={() => handleMoveCategory(idx, 'up')}
                                                disabled={idx === 0}
                                                className="text-slate-600 hover:text-white disabled:opacity-30"
                                            >
                                                <ArrowUp size={14} />
                                            </button>
                                            <button
                                                onClick={() => handleMoveCategory(idx, 'down')}
                                                disabled={idx === categories.length - 1}
                                                className="text-slate-600 hover:text-white disabled:opacity-30"
                                            >
                                                <ArrowDown size={14} />
                                            </button>
                                        </div>
                                        <div className="font-bold text-[#E6C299] text-sm tracking-wide">
                                            {cat.name}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => {
                                                setEditingCatIndex(idx);
                                                setEditCatDraft(cat);
                                            }}
                                            className="text-slate-500 hover:text-[#E6C299] p-1"
                                        >
                                            <Edit2 size={14} />
                                        </button>
                                        <button
                                            onClick={() => handleDeleteCategory(cat.name)}
                                            className="text-slate-500 hover:text-red-500 p-1"
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    ))}
                </div>
                <div className="flex items-center gap-2 pt-2 border-t border-[#222]">
                    <input
                        type="text"
                        value={newCategoryName}
                        onChange={(e) => setNewCategoryName(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleAddCategory()}
                        placeholder="Neue Download-Rubrik…"
                        className="bg-black border border-slate-700 text-white text-xs px-3 py-2 rounded focus:border-[#E6C299] outline-none w-64"
                    />
                    <button
                        onClick={handleAddCategory}
                        disabled={!newCategoryName.trim()}
                        className="bg-slate-800 hover:bg-slate-700 text-white px-3 py-2 rounded border border-slate-700 disabled:opacity-50 flex items-center gap-1 text-xs font-bold uppercase tracking-wider"
                    >
                        <Plus size={14} /> Hinzufügen
                    </button>
                </div>
            </div>

            <div className="flex justify-between items-center mb-4 mt-8 border-t border-[#222] pt-6">
                <div>
                    <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">
                        2. Dateien eintragen
                    </h4>
                    <p className="text-xs text-slate-500 max-w-xl leading-relaxed">
                        Storage: <span className="font-mono text-slate-400">academy_downloads/</span>
                    </p>
                </div>
                {!editing && (
                    <UI.Button onClick={handleAddNew} className="ml-auto shrink-0">
                        + Download hinzufügen
                    </UI.Button>
                )}
            </div>

            {editing && (
                <div className="bg-black p-6 rounded-lg border border-slate-700 mt-2 space-y-4 mb-6 shadow-2xl relative">
                    <div className="absolute top-0 left-0 w-1 h-full bg-[#E6C299] rounded-l-lg" />
                    <h4 className="text-sm font-bold text-[#E6C299] uppercase tracking-wider mb-4 border-b border-gray-800 pb-2">
                        {editing.id ? 'Download bearbeiten' : 'Neuer Download'}
                    </h4>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="md:col-span-2">
                            <span className="text-[10px] text-gray-400 uppercase font-bold mb-1 block">Titel *</span>
                            <UI.Input
                                value={editing.title || ''}
                                onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                                placeholder="z.B. Checkliste Kalkulation"
                            />
                        </div>
                        <div className="md:col-span-2 bg-[#050505] p-4 border border-slate-800 rounded-lg">
                            <span className="text-[10px] text-gray-400 uppercase font-bold mb-1 block">Rubrik *</span>
                            <select
                                value={editing.category || categories[0]?.name || ''}
                                onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                                className="w-full bg-[#0a0a0c] border border-slate-700 text-white text-sm font-bold rounded-lg px-4 py-3 appearance-none cursor-pointer focus:outline-none focus:border-[#E6C299]"
                            >
                                {categories.map((cat) => (
                                    <option key={cat.name} value={cat.name}>
                                        {cat.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="md:col-span-2">
                            <span className="text-[10px] text-gray-400 uppercase font-bold mb-1 block">Beschreibung</span>
                            <UI.Input
                                value={editing.description || ''}
                                onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                                placeholder="Kurzbeschreibung für Mandanten"
                            />
                        </div>
                        <div className="md:col-span-2">
                            <span className="text-[10px] text-gray-400 uppercase font-bold mb-2 block">
                                Datei (PDF, ZIP, Excel, Bild…)
                            </span>
                            <label className="flex flex-col items-center justify-center gap-2 border border-dashed border-slate-600 hover:border-[#E6C299]/50 rounded-xl p-6 cursor-pointer bg-[#050505] transition-colors">
                                <Upload className="w-6 h-6 text-[#E6C299]" />
                                <span className="text-xs text-slate-400 font-bold uppercase tracking-wider">
                                    {pendingFile
                                        ? pendingFile.name
                                        : editing.fileName || editing.fileUrl
                                          ? 'Neue Datei wählen (ersetzt die aktuelle)'
                                          : 'Datei auswählen'}
                                </span>
                                <input
                                    type="file"
                                    className="hidden"
                                    accept=".pdf,.zip,.rar,.7z,.xlsx,.xls,.csv,.png,.jpg,.jpeg,.gif,.webp,.doc,.docx,.ppt,.pptx"
                                    onChange={(e) => handleFilePick(e.target.files?.[0] || null)}
                                />
                            </label>
                            {editing.fileUrl && !pendingFile && (
                                <p className="text-[10px] text-emerald-500/80 mt-2 font-mono truncate">
                                    Aktuell: {editing.fileName || editing.fileUrl}
                                </p>
                            )}
                        </div>
                        <div className="md:col-span-2">
                            <span className="text-[10px] text-gray-400 uppercase font-bold mb-2 block">
                                Vorschaubild (optional)
                            </span>
                            <p className="text-[10px] text-slate-500 mb-2">
                                Für PDFs/ZIP empfohlen. Bei Bild-Dateien wird die Datei automatisch als Vorschau genutzt.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
                                <label className="flex-1 flex flex-col items-center justify-center gap-2 border border-dashed border-slate-600 hover:border-[#E6C299]/50 rounded-xl p-4 cursor-pointer bg-[#050505] transition-colors">
                                    <Upload className="w-5 h-5 text-[#E6C299]" />
                                    <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider text-center px-2">
                                        {pendingThumb
                                            ? pendingThumb.name
                                            : editing.thumbnailUrl
                                              ? 'Vorschaubild ersetzen'
                                              : 'Vorschaubild wählen'}
                                    </span>
                                    <input
                                        type="file"
                                        className="hidden"
                                        accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
                                        onChange={(e) => handleThumbPick(e.target.files?.[0] || null)}
                                    />
                                </label>
                                {(editing.thumbnailUrl || pendingThumb) && (
                                    <div className="relative w-28 h-20 rounded-lg overflow-hidden border border-slate-700 bg-black shrink-0">
                                        <img
                                            src={
                                                pendingThumb
                                                    ? URL.createObjectURL(pendingThumb)
                                                    : editing.thumbnailUrl || ''
                                            }
                                            alt="Vorschau"
                                            className="w-full h-full object-cover"
                                        />
                                        {editing.thumbnailUrl && !pendingThumb && (
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    setEditing({ ...editing, thumbnailUrl: '' })
                                                }
                                                className="absolute top-1 right-1 bg-black/70 text-red-400 rounded p-0.5 hover:text-red-300"
                                                title="Vorschaubild entfernen"
                                            >
                                                <X size={12} />
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {uploadError && (
                        <p className="text-red-400 text-xs font-bold">{uploadError}</p>
                    )}

                    <div className="flex justify-end gap-3 pt-4 mt-2 border-t border-gray-800">
                        <UI.Button
                            variant="secondary"
                            onClick={() => {
                                setEditing(null);
                                setPendingFile(null);
                                setPendingThumb(null);
                                setUploadError(null);
                            }}
                            disabled={isUploading}
                        >
                            Abbrechen
                        </UI.Button>
                        <UI.Button
                            variant="primary"
                            onClick={handleSave}
                            disabled={isUploading || !editing.title?.trim() || (!editing.fileUrl && !pendingFile)}
                        >
                            {isUploading ? (
                                <span className="inline-flex items-center gap-2">
                                    <Loader2 className="w-4 h-4 animate-spin" /> Speichern…
                                </span>
                            ) : (
                                'Speichern'
                            )}
                        </UI.Button>
                    </div>
                </div>
            )}

            <div className="space-y-2">
                {downloads.length === 0 && !editing && (
                    <p className="text-gray-600 text-center py-4 text-sm italic">Keine Downloads vorhanden.</p>
                )}
                {downloads.map((item, idx) => (
                    <div
                        key={item.id}
                        className="bg-gray-800/50 p-3 rounded border border-gray-700 flex items-center justify-between flex-wrap gap-2 hover:bg-gray-800 transition-colors"
                    >
                        <div className="flex items-center gap-3 min-w-0">
                            {(item.thumbnailUrl || item.fileType === 'image') && (
                                <div className="w-12 h-12 rounded border border-slate-700 overflow-hidden bg-black shrink-0">
                                    <img
                                        src={item.thumbnailUrl || item.fileUrl}
                                        alt=""
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                            )}
                            <div className="min-w-0">
                            <div className="flex items-center gap-3 flex-wrap">
                                <p className="font-bold text-white text-sm">{item.title}</p>
                                <span className="bg-[#E6C299]/10 text-[#E6C299] border border-[#E6C299]/30 text-[9px] px-2 py-0.5 rounded font-black uppercase tracking-widest">
                                    {item.category || 'Allgemein'}
                                </span>
                                <span className="bg-slate-900 text-slate-400 border border-slate-700 text-[9px] px-2 py-0.5 rounded font-mono uppercase">
                                    {item.fileType}
                                </span>
                            </div>
                            <p className="text-xs text-gray-500 truncate max-w-md font-mono mt-1">
                                {item.fileName || item.fileUrl}
                            </p>
                            </div>
                        </div>
                        <div className="flex gap-2 items-center">
                            <div className="flex flex-col leading-none">
                                <button
                                    type="button"
                                    onClick={() => handleMove(idx, 'up')}
                                    disabled={idx === 0}
                                    className="text-slate-500 hover:text-[#E6C299] disabled:opacity-30 p-1"
                                >
                                    <ArrowUp size={14} />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => handleMove(idx, 'down')}
                                    disabled={idx === downloads.length - 1}
                                    className="text-slate-500 hover:text-[#E6C299] disabled:opacity-30 p-1"
                                >
                                    <ArrowDown size={14} />
                                </button>
                            </div>
                            <a
                                href={item.fileUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-slate-500 hover:text-[#E6C299] p-1"
                                title="Öffnen"
                            >
                                <Download size={14} />
                            </a>
                            <button
                                type="button"
                                onClick={() => {
                                    setPendingFile(null);
                                    setPendingThumb(null);
                                    setEditing({ ...item });
                                }}
                                className="text-slate-500 hover:text-[#E6C299] p-1"
                            >
                                <Edit2 size={14} />
                            </button>
                            <button
                                type="button"
                                onClick={() => handleDelete(item)}
                                className="text-slate-500 hover:text-red-500 p-1"
                            >
                                <Trash2 size={14} />
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </>
    );
};

const LogoManagement = () => {
    const { appSettings, updateAppSettings, captureState } = useAppStore(state => ({
        appSettings: state.appSettings,
        updateAppSettings: state.updateAppSettings,
        captureState: state.captureState
    }));

    const handleLogoChange = (key: keyof AppSettings, value: string) => {
        captureState(); 
        updateAppSettings({ [key]: value });
    };

    return (
        <div className="space-y-4">
            <div className="bg-gray-800/30 p-4 rounded border border-gray-700">
                <span className="text-[10px] text-gray-400 uppercase font-bold mb-1 block">Team / Solo Logo URL</span>
                <UI.Input value={appSettings?.logoUrl || ''} onChange={(e) => handleLogoChange('logoUrl', e.target.value)} placeholder="https://..." />
            </div>
            <div className="bg-gray-800/30 p-4 rounded border border-gray-700">
                <span className="text-[10px] text-gray-400 uppercase font-bold mb-1 block">Base Logo URL</span>
                <UI.Input value={appSettings?.basicLogoUrl || ''} onChange={(e) => handleLogoChange('basicLogoUrl', e.target.value)} placeholder="https://..." />
            </div>
        </div>
    );
};

type FieldType = "number" | "percentage" | "currency" | "text";

const countryParameterKeys: { key: string; label: string; type: FieldType }[] = [
    { key: 'Lohnnebenkosten', label: 'Lohnnebenkosten', type: 'percentage' },
    { key: 'Lohnnebenkosten_Minijob', label: 'Lohnnebenkosten Minijob', type: 'percentage' },
    { key: 'MwSt_Satz', label: 'MwSt. Satz', type: 'percentage' },
    { key: 'Minijob_Lohn_Grenze', label: 'Minijob Lohn Grenze', type: 'currency' },
    { key: 'Mindestlohn', label: 'Mindestlohn', type: 'currency' },
];

const parameterMetadata: { [key: string]: { type: FieldType; label: string; } } = {
    'Warenwirtschaftsaufschlag_VK': { type: 'number', label: 'Warenaufschlag VK' },
    'Warenwirtschaftsaufschlag_DL': { type: 'number', label: 'Warenaufschlag DL' },
    'Investlinie': { type: 'percentage', label: 'Investlinie' },
    'Gewinnlinie': { type: 'percentage', label: 'Gewinnlinie' },
    'Zusatzumsatz': { type: 'currency', label: 'Zusatzumsatz' },
    'Verlust_1': { type: 'percentage', label: 'Verlust 1' },
    'Liquiditaet': { type: 'percentage', label: 'Liquidität' },
    'Wochensatz': { type: 'number', label: 'Wochensatz' },
    'Urlaubsausgleich': { type: 'percentage', label: 'Urlaubsausgleich' },
    'Auslastungsfaktor': { type: 'percentage', label: 'Auslastungsfaktor' },
    'Auslastungsfaktor_Max': { type: 'percentage', label: 'Auslastungsfaktor Max' },
    'Insider_Team': { type: 'currency', label: 'Preis: Team' },
    'Insider_Solo': { type: 'currency', label: 'Preis: Solo' },
    'Neukunden_Aufschlag': { type: 'percentage', label: 'Neukunden Aufschlag 1. Jahr (%)' },
    'Grobkundenaufschlag': { type: 'number', label: 'Großkundenaufschlag (%)' }, 
    'Grobkundenaufschlag_ab': { type: 'currency', label: 'Schwelle (Auto-Berechnet ab "Preis: Team")' }, 
    'Tagessatz': { type: 'currency', label: 'Tagessatz (§ 11)' },
    'Stundensatz': { type: 'currency', label: 'Stundensatz (§ 11)' },
    'KmRate': { type: 'currency', label: 'Km-Pauschale (€) (§ 11)' }, 
};

const webdesignPackageKeys: { key: string; label: string; type: FieldType }[] = [
    { key: 'basic_mon_12', label: 'Basic Monat (12 M.)', type: 'currency' }, { key: 'basic_gesamt_12', label: 'Basic Gesamt (12 M.)', type: 'currency' }, { key: 'basic_zs_12', label: 'Basic ZS (12 M.)', type: 'currency' },
    { key: 'basic_mon_24', label: 'Basic Monat (24 M.)', type: 'currency' }, { key: 'basic_gesamt_24', label: 'Basic Gesamt (24 M.)', type: 'currency' }, { key: 'basic_zs_24', label: 'Basic ZS (24 M.)', type: 'currency' },
    { key: 'indiv_mon_12', label: 'Individuell Monat (12 M.)', type: 'currency' }, { key: 'indiv_gesamt_12', label: 'Individuell Gesamt (12 M.)', type: 'currency' }, { key: 'indiv_zs_12', label: 'Individuell ZS (12 M.)', type: 'currency' },
    { key: 'indiv_mon_24', label: 'Individuell Monat (24 M.)', type: 'currency' }, { key: 'indiv_gesamt_24', label: 'Individuell Gesamt (24 M.)', type: 'currency' }, { key: 'indiv_zs_24', label: 'Individuell ZS (24 M.)', type: 'currency' },
    { key: 'seo_monatl_12', label: 'SEO Monatlich (12 M.)', type: 'currency' }, { key: 'seo_monatl_24', label: 'SEO Monatlich (24 M.)', type: 'currency' }, { key: 'seo_gesamt', label: 'SEO Gesamt', type: 'currency' },
];

type SectionName = 'global' | 'country' | 'webdesignPackages';
type Parameter = { key: string; label: string; type: FieldType; };

const KalkulationsdatenPage = () => {
    const { settings, updateSettings, updateNestedSettings, captureState, frozenUntilMonth, setClientData, clientData } = useAppStore(state => ({ 
        settings: state.settings, 
        updateSettings: state.updateSettings, 
        updateNestedSettings: state.updateNestedSettings,
        captureState: state.captureState,
        frozenUntilMonth: (state as any).clientData?.frozenUntilMonth,
        setClientData: state.setClientData,
        clientData: state.clientData,
    }));

    const [isDangerZoneOpen, setIsDangerZoneOpen] = useState(false);
    const [sweepMonth, setSweepMonth] = useState<number | ''>('');
    const [sweepPassword, setSweepPassword] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const isSweepUnlocked = sweepPassword === '90';

    const monthNames = ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"];

    const handleUnfreeze = async () => {
        if (!window.confirm("⚠️ Wirklich aufheben? Dies löscht alle Freeze-Stempel bei ALLEN Mandanten in der Datenbank!")) return;
        
        setIsProcessing(true);
        
        captureState();

        const state = useAppStore.getState();
        const cd = JSON.parse(JSON.stringify(state.clientData)); 
        delete cd.frozenUntilMonth;
        setClientData(cd);

        const successCount = await executeGlobalMassUnfreeze();
        
        if (successCount >= 0) {
            alert(`✅ ERFOLG: Vergangenheit wurde für ${successCount} Akten entsperrt!\n\n⚠️ GANZ WICHTIG: Klicke jetzt oben auf das SPEICHERN-SYMBOL, um das auch für deine eigene Akte final in die Cloud zu brennen!`);
        } else {
            alert("❌ Fehler beim Synchronisieren der Daten mit Firebase.");
        }
        setIsProcessing(false);
    };

    const handleExportJSON = () => {
        const state = useAppStore.getState();
        const payload = {
            settings: state.settings,
            team: state.team,
            appSettings: state.appSettings
        };
        const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(payload, null, 2));
        const downloadElement = document.createElement('a');
        downloadElement.setAttribute("href", dataStr);
        downloadElement.setAttribute("download", `preisexpert_master_backup_${new Date().toISOString().split('T')[0]}.json`);
        document.body.appendChild(downloadElement);
        downloadElement.click();
        downloadElement.remove();
    };

    // 💉 CHIRURGIE: Lokaler, robuster FileReader statt anfälliger Store-Action
    const handleGlobalJSONImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = async (event) => {
            try {
                const jsonString = event.target?.result as string;
                let parsedData;
                
                try {
                    parsedData = JSON.parse(jsonString);
                } catch (parseErr: any) {
                    console.error("SyntaxError: Unexpected token beim Master-Backup.", parseErr);
                    alert("❌ Import fehlgeschlagen: Die Datei ist kein gültiges JSON.");
                    return;
                }

                const isUsableValue = (value: unknown): boolean => {
                    if (value == null) return false;
                    if (Array.isArray(value)) return value.length > 0;
                    if (typeof value === 'object') return Object.keys(value as object).length > 0;
                    return true;
                };

                const systemLayer =
                    parsedData?.system_data && typeof parsedData.system_data === 'object'
                        ? parsedData.system_data
                        : null;

                const resolveImportField = (key: 'settings' | 'team' | 'teamRegistry' | 'appSettings') => {
                    // 1) Top-Level Keys
                    if (isUsableValue(parsedData?.[key])) return parsedData[key];
                    // 2) system_data
                    if (systemLayer && isUsableValue(systemLayer[key])) return systemLayer[key];
                    // 3) Root als flaches Firestore-Dokument
                    if (isUsableValue(parsedData?.[key])) return parsedData[key];
                    return undefined;
                };

                const importedSettings = resolveImportField('settings');
                const importedTeam = resolveImportField('team');
                const importedTeamRegistry = resolveImportField('teamRegistry');
                const importedAppSettings = resolveImportField('appSettings');

                if (
                    importedSettings === undefined &&
                    importedTeam === undefined &&
                    importedTeamRegistry === undefined &&
                    importedAppSettings === undefined
                ) {
                    throw new Error(
                        "Fehlende Master-Daten. Das JSON enthält weder 'settings', 'team', 'teamRegistry' noch 'appSettings'."
                    );
                }

                const currentState = useAppStore.getState();

                // 💉 CHIRURGIE: Direkter State-Inject, um die defekte 'importClientData' Store-Action zu umgehen
                useAppStore.setState({
                    settings: importedSettings !== undefined ? importedSettings : currentState.settings,
                    team: importedTeam !== undefined ? importedTeam : currentState.team,
                    teamRegistry:
                        importedTeamRegistry !== undefined
                            ? importedTeamRegistry
                            : currentState.teamRegistry,
                    appSettings:
                        importedAppSettings !== undefined
                            ? importedAppSettings
                            : currentState.appSettings,
                });

                console.log("💉 Master-Backup erfolgreich geladen:", {
                    settings: importedSettings !== undefined,
                    team: importedTeam !== undefined,
                    teamRegistry: importedTeamRegistry !== undefined,
                    appSettings: importedAppSettings !== undefined,
                    parsedData,
                });
                alert("✅ JSON erfolgreich geladen!\n\nDie Daten (Team, Logos, Parameter) sind jetzt auf dem Bildschirm.\n\n⚠️ WICHTIG: Drücke jetzt den blauen Button '🔥 IN CLOUD SPEICHERN', um sie dauerhaft in Firebase zu brennen!");

            } catch (err: any) {
                console.error("Fehler beim Verarbeiten des Master-Backups:", err);
                alert(`❌ Fehler beim Laden der Datei: ${err.message}`);
            } finally {
                e.target.value = '';
            }
        };

        reader.onerror = (err) => {
            console.error("FileReader Fehler:", err);
            alert("❌ Lokaler Dateilesefehler.");
            e.target.value = '';
        };

        reader.readAsText(file);
    };

    const stripUndefinedDeep = (value: unknown): unknown => {
        if (Array.isArray(value)) {
            return value.map(stripUndefinedDeep);
        }
        if (value && typeof value === 'object') {
            const out: Record<string, unknown> = {};
            Object.entries(value as Record<string, unknown>).forEach(([key, entry]) => {
                if (entry === undefined) return;
                out[key] = stripUndefinedDeep(entry);
            });
            return out;
        }
        return value;
    };

    const handleForceSync = async () => {
        try {
            const state = useAppStore.getState();
            const payload = stripUndefinedDeep({
                settings: state.settings,
                team: state.team,
                teamRegistry: state.teamRegistry || [],
                appSettings: state.appSettings
            });
            
            const sysRef = doc(db, 'system_data', 'global_settings');
            await setDoc(sysRef, payload as any, { merge: true });
            
            alert("✅ ERFOLG: Die Master-Daten wurden sicher in Firebase unter 'system_data' geschrieben. Alle noch nicht eingefrorenen Monate und Live-Tools rechnen ab sofort mit diesen neuen Werten.");
        } catch (e: any) {
            console.error(e);
            alert("❌ FEHLER beim Schreiben in Firebase: " + e.message);
        }
    };

    if (!settings) return <UI.Page><div className="flex justify-center items-center h-screen"><div className="text-[#E6C299] animate-pulse font-bold uppercase tracking-widest">Lade Master-Konfiguration...</div></div></UI.Page>;

    const handleParameterChange = (section: SectionName, key: string, newValue: string | number, countryId?: string) => {
        captureState(); 

        let numericValue: number;
        
        if (typeof newValue === 'string') {
             numericValue = Number(newValue.replace(',', '.')) || 0;
        } else {
            numericValue = newValue;
        }

        const isWebdesign = section === 'webdesignPackages';
        
        const places = (key === 'KmRate' || key === 'Mindestlohn' || key === 'Minijob_Lohn_Grenze' || isWebdesign) ? 2
                      : (key === 'Grobkundenaufschlag' ? 2 
                      : (parameterMetadata[key]?.type === 'currency' || countryParameterKeys.find(p => p.key === key)?.type === 'currency' ? 0 
                      : 2)); 

        const factor = Math.pow(10, places);
        numericValue = Math.round(numericValue * factor) / factor;

        // Logik für die Auto-Berechnung der Schwelle, wenn sich Preis oder Prozentwert ändert
        if (section === 'global' && (key === 'Grobkundenaufschlag' || key === 'Insider_Team')) {
            const globalSettings = settings.global || {};
            const basePrice = key === 'Insider_Team' ? numericValue : (globalSettings['Insider_Team'] || 199);
            const percentage = key === 'Grobkundenaufschlag' ? numericValue : (globalSettings['Grobkundenaufschlag'] || 0.5);
            
            const newThreshold = percentage > 0 ? (basePrice / (percentage / 100)) : 0;
            const roundedThreshold = Math.round(newThreshold);

            updateNestedSettings('global', 'Grobkundenaufschlag_ab', roundedThreshold);
        }

        if (section === 'global' || section === 'webdesignPackages') {
            updateNestedSettings(section, key, numericValue);
        } else if (section === 'country' && countryId) {
            const countryOverrides = settings.countryOverrides?.[countryId] || {};
            const newOverrides = { ...settings.countryOverrides, [countryId]: { ...countryOverrides, [key]: numericValue }};
            updateSettings('countryOverrides', newOverrides);
        }
    };

    const renderSectionContent = (params: Parameter[], sectionName: SectionName, countryId?: string) => (
        <div className="grid grid-cols-1 gap-y-3 md:grid-cols-2 md:gap-x-12">
            {params.map(p => {
                let valueSource: any = {};
                let currencyCode = 'EUR'; 

                if (sectionName === 'country' && countryId) {
                    valueSource = { ...(settings?.countries?.[countryId] || {}), ...(settings?.countryOverrides?.[countryId] || {}) };
                    currencyCode = settings?.countries?.[countryId]?.currency || (countryId === 'CH' ? 'CHF' : 'EUR');
                } else if (sectionName === 'global') {
                    valueSource = settings?.global || {};
                    currencyCode = 'EUR'; 
                } else if (sectionName === 'webdesignPackages') {
                    valueSource = settings?.webdesignPackages || {};
                    currencyCode = 'EUR';
                }

                let value = valueSource && typeof valueSource === 'object' && p.key in valueSource
                    ? valueSource[p.key as keyof typeof valueSource]
                    : 0; 

                // 💉 CHIRURGIE: Live-Berechnung erzwingen, Geister-Daten aus DB ignorieren!
                if (p.key === 'Grobkundenaufschlag_ab' && sectionName === 'global') {
                    const basePrice = settings?.global?.['Insider_Team'] || 199;
                    const percentage = settings?.global?.['Grobkundenaufschlag'] || 0.5;
                    // Schwelle = Basispreis / (Prozentsatz / 100)
                    value = percentage > 0 ? Math.round(basePrice / (percentage / 100)) : 0;
                }

                // Die Schwelle wird in der UI gesperrt (Read-Only)
                const isReadOnly = p.key === 'Grobkundenaufschlag_ab';

                return (
                    <div key={`${sectionName}-${countryId || 'global'}-${p.key}`} className="flex items-center justify-between py-1 hover:bg-white/5 rounded transition-colors px-2 -mx-2 group">
                          <span className={`text-xs font-bold uppercase tracking-wide w-1/2 pr-4 transition-colors ${isReadOnly ? 'text-blue-400' : 'text-gray-400 group-hover:text-white'}`}>{p.label}</span>
                          <div className={`w-40 h-10 ${isReadOnly ? 'opacity-70 pointer-events-none' : ''}`}>
                             <EditableField 
                                value={value} 
                                onChange={(val: any) => handleParameterChange(sectionName, p.key, val, countryId)} 
                                type={p.type}
                                currency={currencyCode}
                                fieldKey={p.key}
                                readOnly={isReadOnly}
                             />
                          </div>
                    </div>
                );
            })}
        </div>
    );

    const dynamicGlobalParams: Parameter[] = Object.keys(parameterMetadata)
        .filter(key => key in (settings?.global || {}) || key === 'Neukunden_Aufschlag') 
        .map(key => ({
            key: key,
            label: parameterMetadata[key].label,
            type: parameterMetadata[key].type,
        }));

    const countryList = ['DE', 'AT', 'CH', 'IT'];
    const getCountryName = (code: string) => {
        switch(code) {
            case 'DE': return 'Deutschland (DE)';
            case 'AT': return 'Österreich (AT)';
            case 'CH': return 'Schweiz (CH)';
            case 'IT': return 'Italien (IT)';
            default: return code;
        }
    };

    return (
        <UI.Page>
            <UI.Section>
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 bg-blue-900/20 p-6 rounded-xl border border-blue-500/30 gap-4 shadow-lg">
                    <div className="flex-1">
                        <h2 className="text-xl font-black text-blue-400 uppercase tracking-widest flex items-center gap-2">
                            <span>📡</span> Master-Sync & Backup
                        </h2>
                        <p className="text-xs text-gray-400 mt-1 max-w-xl leading-relaxed">Erstelle ein Backup deiner Settings, lade ein altes Backup (JSON) oder brenne deine Eingaben permanent in die Cloud.</p>
                    </div>
                    
                    <div className="flex flex-wrap gap-2 w-full md:w-auto">
                        <button 
                            onClick={handleExportJSON}
                            className="flex-1 md:flex-none bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold py-2 px-4 rounded cursor-pointer transition-all flex items-center justify-center gap-2 border border-slate-600 text-xs"
                        >
                            <span className="text-sm">💾</span> BACKUP SICHERN
                        </button>
                        <label className="flex-1 md:flex-none bg-slate-800 hover:bg-slate-700 text-white font-bold py-2 px-4 rounded cursor-pointer transition-all flex items-center justify-center gap-2 border border-slate-600 text-xs shadow-[0_0_10px_rgba(0,0,0,0.5)]">
                            <span className="text-sm">📁</span> JSON LADEN
                            {/* 💉 CHIRURGIE: Neuer handleGlobalJSONImport Call */}
                            <input type="file" accept=".json" onChange={handleGlobalJSONImport} className="hidden" />
                        </label>
                        <button 
                            onClick={handleForceSync}
                            className="flex-1 md:flex-none bg-blue-600 hover:bg-blue-500 text-white font-black py-2 px-4 rounded shadow-[0_0_15px_rgba(37,99,235,0.3)] transition-all active:scale-95 flex items-center justify-center gap-2 border border-blue-400 text-xs"
                        >
                            <span className="text-sm">🔥</span> IN CLOUD SPEICHERN
                        </button>
                    </div>
                </div>

                {frozenUntilMonth !== undefined && frozenUntilMonth > 0 && (
                    <div className="bg-emerald-950/30 border border-emerald-800 p-4 rounded-xl mb-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 shadow-xl">
                        <div>
                            <h4 className="text-emerald-400 font-bold flex items-center gap-2 uppercase tracking-widest text-sm">
                                <span>✅</span> Historie ist eingefroren
                            </h4>
                            <p className="text-emerald-300/80 text-xs mt-1">
                                Die vergangenen Monate bis einschließlich <strong>{monthNames[frozenUntilMonth - 1]}</strong> sind mit den historischen Master-Daten gesichert.
                            </p>
                        </div>
                        <button 
                            onClick={handleUnfreeze} 
                            disabled={isProcessing}
                            className="bg-red-900/50 hover:bg-red-600 text-white text-xs font-bold px-4 py-2 rounded transition-colors border border-red-800 tracking-wider disabled:opacity-50"
                        >
                            {isProcessing ? '⚙️ ENTSPERRE...' : 'Schutz aufheben'}
                        </button>
                    </div>
                )}

                <div className="bg-[#1a0505] border border-red-900/50 rounded-xl mb-12 shadow-2xl relative overflow-hidden transition-all duration-300">
                    <div className="absolute top-0 left-0 w-1 h-full bg-red-600"></div>
                    
                    <button 
                        onClick={() => setIsDangerZoneOpen(!isDangerZoneOpen)}
                        className="w-full flex justify-between items-center p-4 hover:bg-red-900/10 transition-colors focus:outline-none"
                    >
                        <h3 className="text-red-500 font-black uppercase tracking-widest text-sm flex items-center gap-2">
                            <span>⚠️</span> GEFAHRENZONE: Vergangenheit des laufenden Jahres für ALLE Mandanten einfrieren
                        </h3>
                        <span className="text-red-500 font-bold">{isDangerZoneOpen ? '▲ SCHLIESSEN' : '▼ ÖFFNEN'}</span>
                    </button>

                    {isDangerZoneOpen && (
                        <div className="p-6 pt-0 border-t border-red-900/30">
                            <p className="text-xs text-red-300/70 mb-4 mt-4 max-w-3xl leading-relaxed">
                                Nutze diesen Schutz-Modus <strong>NUR BEVOR</strong> du unten neue Werte einträgst (z.B. bei unterjähriger MwSt-Erhöhung im Juli). Dieser Button nimmt die aktuell noch gültigen alten Werte und friert damit alle vergangenen Monate <strong>VOR</strong> dem Stichtag dauerhaft ein – und zwar bei <strong>ALLEN</strong> Mandanten im gesamten System.
                            </p>

                            <div className="flex flex-col md:flex-row items-start md:items-end gap-4 bg-black/40 p-4 rounded border border-red-900/30">
                                <div className="flex flex-col gap-1">
                                    <span className="text-[10px] text-gray-500 uppercase font-bold tracking-widest">Sicherheits-Code</span>
                                    <input
                                        type="password"
                                        value={sweepPassword}
                                        onChange={(e) => setSweepPassword(e.target.value)}
                                        placeholder="***"
                                        className="bg-black border border-slate-700 text-red-400 p-2 rounded focus:border-red-500 outline-none font-bold text-sm w-24 text-center"
                                        disabled={isProcessing}
                                    />
                                </div>

                                {isSweepUnlocked ? (
                                    <>
                                        <div className="flex flex-col gap-1 w-full md:w-64 animate-fade-in">
                                            <span className="text-[10px] text-red-400 uppercase font-bold tracking-widest">Ab wann gibt es neue Werte? (Stichtag)</span>
                                            <select
                                                value={sweepMonth}
                                                onChange={(e) => setSweepMonth(e.target.value === '' ? '' : parseInt(e.target.value))}
                                                className="bg-red-950/20 border border-red-800 text-white p-2 rounded focus:border-red-500 outline-none font-bold text-sm w-full cursor-pointer"
                                                disabled={isProcessing}
                                            >
                                                <option value="" disabled>-- STICHTAG WÄHLEN --</option>
                                                <option value="0">Januar</option>
                                                <option value="1">Februar</option>
                                                <option value="2">März</option>
                                                <option value="3">April</option>
                                                <option value="4">Mai</option>
                                                <option value="5">Juni</option>
                                                <option value="6">Juli (Sichert Jan-Jun)</option>
                                                <option value="7">August</option>
                                                <option value="8">September</option>
                                                <option value="9">Oktober</option>
                                                <option value="10">November</option>
                                                <option value="11">Dezember</option>
                                            </select>
                                        </div>
                                        <button
                                            onClick={async () => {
                                                if (sweepMonth === '') {
                                                    alert('❌ Bitte erst einen Stichtag (Monat) auswählen!');
                                                    return;
                                                }
                                                if(window.confirm(`⚠️ ACHTUNG MASSEN-UPDATE: Sollen alle Leistungs-Monate VOR dem gewählten Stichtag bei ALLEN MANDANTEN in der Datenbank dauerhaft eingefroren und geschützt werden? Das kann einige Sekunden dauern.`)) {
                                                    
                                                    setIsProcessing(true);
                                                    
                                                    captureState();

                                                    const state = useAppStore.getState();
                                                    const cd = JSON.parse(JSON.stringify(state.clientData)); 
                                                    cd.frozenUntilMonth = sweepMonth;
                                                    setClientData(cd);

                                                    const successCount = await executeGlobalMassFreeze(sweepMonth as number, settings);

                                                    if (successCount >= 0) {
                                                        alert(`✅ ERFOLG: Akten erfolgreich eingefroren!\n\n⚠️ WICHTIG: Klicke jetzt oben auf das SPEICHERN-SYMBOL, um deine eigene Akte final in die Cloud zu brennen!`);
                                                        setSweepMonth('');
                                                        setSweepPassword('');
                                                        setIsDangerZoneOpen(false);
                                                    } else {
                                                        alert('❌ FEHLER: Das Massen-Update konnte nicht fehlerfrei durchgeführt werden. Bitte Konsole prüfen.');
                                                    }
                                                    setIsProcessing(false);
                                                }
                                            }}
                                            disabled={sweepMonth === '' || isProcessing}
                                            className="bg-red-600 hover:bg-red-500 text-white border border-red-400 font-black py-2 px-6 rounded transition-all active:scale-95 uppercase tracking-widest text-xs disabled:opacity-50 disabled:cursor-not-allowed animate-fade-in flex items-center gap-2"
                                        >
                                            {isProcessing ? '⚙️ VERARBEITE...' : '❄️ ALLE MANDANTEN EINFRIEREN'}
                                        </button>
                                    </>
                                ) : (
                                    <div className="flex items-center h-10 px-4 text-xs font-bold text-red-800 uppercase tracking-widest bg-red-950/10 rounded border border-red-900/20">
                                        Bereich gesperrt
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                <UI.PageTitle>System & Organisation</UI.PageTitle>
                
                <div className="mb-12">
                    <UI.CollapsibleSection title="Team-Verwaltung">
                        <TeamManagement />
                    </UI.CollapsibleSection>

                    <UI.CollapsibleSection title="Geldfluss-Matrix (Interne Verteilung)">
                        <DynamicShareManagement />
                    </UI.CollapsibleSection>
                    
                    <UI.CollapsibleSection title="Video-Tutorials verwalten">
                        <VideoManagement />
                    </UI.CollapsibleSection>

                    <UI.CollapsibleSection title="Download-Materialien verwalten">
                        <DownloadManagement />
                    </UI.CollapsibleSection>

                    <UI.CollapsibleSection title="Logo-Verwaltung">
                        <LogoManagement />
                    </UI.CollapsibleSection>
                </div>

                <div className="my-8 pt-8 border-t border-gray-800">
                    <div className="mb-6">
                        <h2 className="text-2xl font-black text-white uppercase tracking-widest flex items-center gap-3 mb-2">
                            <span className="text-[#E6C299]">⚙️</span> Parameter Steuerung
                        </h2>
                        <p className="text-slate-400 text-sm">
                            Das ist der Maschinenraum. Änderungen hier wirken sich auf **neue** Berechnungen aus. Der Länderschalter wurde entfernt: Alle Ländereinstellungen sind jetzt zeitgleich und individuell justierbar.
                        </p>
                    </div>

                    <div className="space-y-6">
                        <UI.CollapsibleSection title="Globale Kalkulations-Parameter">
                            {renderSectionContent(dynamicGlobalParams, 'global')}
                        </UI.CollapsibleSection>

                        <UI.CollapsibleSection title="Webdesign-Pakete & SEO">
                            {renderSectionContent(webdesignPackageKeys, 'webdesignPackages')}
                        </UI.CollapsibleSection>

                        <div className="pt-6 mt-6 border-t border-gray-800">
                            <h3 className="text-sm font-black text-[#E6C299] uppercase tracking-widest mb-4">Länderspezifische Parameter</h3>
                            
                            <div className="space-y-4">
                                {countryList.map(countryId => {
                                    const itValues = countryId === 'IT'
                                        ? {
                                            ItBz_Faktor_Azubi: 1.42,
                                            ItBz_Faktor_Teilzeit: 1.65,
                                            ItBz_Faktor_Vollzeit: 1.80,
                                            ItBz_Vollzeit_Stundenschwelle: 30,
                                            ...(settings?.countries?.IT || {}),
                                            ...(settings?.countryOverrides?.IT || {}),
                                        }
                                        : null;
                                    const thresholdVal = Number(itValues?.ItBz_Vollzeit_Stundenschwelle) || 30;
                                    const atValues = countryId === 'AT'
                                        ? {
                                            At_Lohnnebenkosten_Fachkraft: 52.0,
                                            At_Lohnnebenkosten_Azubi: 35.0,
                                            Lohnnebenkosten_Minijob: 16.4,
                                            ...(settings?.countries?.AT || {}),
                                            ...(settings?.countryOverrides?.AT || {}),
                                        }
                                        : null;
                                    const chValues = countryId === 'CH'
                                        ? {
                                            Lohnnebenkosten: 17.0,
                                            MwSt_Satz: 8.1,
                                            vatThreshold: 100000,
                                            isVatExempt: true,
                                            voluntaryOption: false,
                                            ...(settings?.countries?.CH || {}),
                                            ...(settings?.countryOverrides?.CH || {}),
                                        }
                                        : null;
                                    const chVorjahresUmsatz = Number(clientData?.bwaData?.umsatz) || 0;
                                    const chThreshold = Number(chValues?.vatThreshold) || 100000;
                                    const chVoluntary = Boolean(chValues?.voluntaryOption);
                                    const chIsVatExempt =
                                        !chVoluntary &&
                                        chVorjahresUmsatz < chThreshold &&
                                        (chValues?.isVatExempt !== undefined ? Boolean(chValues.isVatExempt) : true);
                                    const chEffectiveVat = chIsVatExempt ? 0.0 : (Number(chValues?.MwSt_Satz) || 8.1);

                                    return (
                                        <UI.CollapsibleSection key={countryId} title={`Parameter: ${getCountryName(countryId)}`}>
                                            <div className="p-2">
                                                {renderSectionContent(
                                                    countryId === 'IT'
                                                        ? countryParameterKeys.filter(p => p.key === 'MwSt_Satz')
                                                        : countryId === 'AT'
                                                            ? countryParameterKeys.filter(p => p.key === 'MwSt_Satz' || p.key === 'Minijob_Lohn_Grenze' || p.key === 'Lohnnebenkosten_Minijob')
                                                            : countryId === 'CH'
                                                                ? countryParameterKeys.filter(p => p.key === 'Lohnnebenkosten' || p.key === 'MwSt_Satz')
                                                                : countryParameterKeys,
                                                    'country',
                                                    countryId
                                                )}

                                                {countryId === 'CH' && chValues && (
                                                    <div className="bg-[#15171e] border border-[#dcb68a]/35 rounded-xl p-4 mt-4 shadow-lg">
                                                        <div className="flex flex-wrap items-center justify-between gap-2 pb-3 mb-4 border-b border-[#dcb68a]/20">
                                                            <div className="flex items-center gap-2">
                                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-[10px] font-black uppercase tracking-widest bg-[#dcb68a]/15 text-[#dcb68a] border border-[#dcb68a]/40">
                                                                    CH — ART. 10 MWSTG
                                                                </span>
                                                                <h4 className="text-xs font-black uppercase tracking-widest text-[#dcb68a]">
                                                                    Schweizer MWST-Steuerung (100&apos;000 CHF Schwelle)
                                                                </h4>
                                                            </div>
                                                            <span
                                                                className={`inline-flex items-center px-2.5 py-1 rounded-md text-[10px] font-black uppercase tracking-widest border ${
                                                                    chIsVatExempt
                                                                        ? 'bg-emerald-950/40 text-emerald-400 border-emerald-500/40'
                                                                        : 'bg-[#dcb68a]/20 text-[#dcb68a] border-[#dcb68a]/50'
                                                                }`}
                                                            >
                                                                {chIsVatExempt
                                                                    ? 'Art. 10 MWSTG Befreiung aktiv (0,0 %)'
                                                                    : `MWST-pflichtig (${chEffectiveVat.toFixed(1).replace('.', ',')} %)`}
                                                            </span>
                                                        </div>

                                                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 items-center">
                                                            <div className="space-y-1">
                                                                <p className="text-xs font-bold text-gray-200">
                                                                    {chVorjahresUmsatz < chThreshold
                                                                        ? "Vorjahresumsatz unter 100'000 CHF: Befreiung nach Art. 10 MWSTG aktiv"
                                                                        : "Vorjahresumsatz ab 100'000 CHF: Gesetzliche MWST-Pflicht (Normalsatz 8,1 %)"}
                                                                </p>
                                                                <p className="text-[11px] font-mono text-slate-400">
                                                                    Aktueller BWA-Vorjahresumsatz:{' '}
                                                                    <span className="text-[#dcb68a] font-bold">
                                                                        {chVorjahresUmsatz.toLocaleString('de-CH', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} CHF
                                                                    </span>{' '}
                                                                    (Schwelle: {chThreshold.toLocaleString('de-CH')} CHF · Effektiver Satz: {chEffectiveVat.toFixed(1).replace('.', ',')} %)
                                                                </p>
                                                            </div>

                                                            <div className="flex items-center justify-between md:justify-end gap-4 bg-[#0f1015] border border-slate-800 rounded-lg px-4 py-3">
                                                                <div className="pr-2">
                                                                    <span className="text-xs font-bold uppercase tracking-wide text-gray-200 block">
                                                                        Freiwillige Unterstellung (Optierung)
                                                                    </span>
                                                                    <span className="text-[10px] text-slate-400">
                                                                        Schaltet auch unter 100&apos;000 CHF auf {Number(chValues.MwSt_Satz || 8.1).toFixed(1).replace('.', ',')} % MWST um
                                                                    </span>
                                                                </div>
                                                                <label className="relative inline-flex items-center cursor-pointer shrink-0">
                                                                    <input
                                                                        type="checkbox"
                                                                        className="sr-only peer"
                                                                        checked={chVoluntary || !chIsVatExempt}
                                                                        disabled={chVorjahresUmsatz >= chThreshold}
                                                                        onChange={(e) => {
                                                                            captureState();
                                                                            const checked = e.target.checked;
                                                                            const prevCh = settings.countryOverrides?.CH || {};
                                                                            const newOverrides = {
                                                                                ...settings.countryOverrides,
                                                                                CH: {
                                                                                    ...prevCh,
                                                                                    voluntaryOption: checked,
                                                                                    isVatExempt: !checked,
                                                                                },
                                                                            };
                                                                            updateSettings('countryOverrides', newOverrides);
                                                                        }}
                                                                    />
                                                                    <div className="w-11 h-6 bg-slate-800 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#dcb68a] peer-disabled:opacity-50"></div>
                                                                </label>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}

                                                {countryId === 'AT' && atValues && (
                                                    <div className="bg-[#15171e] border border-[#dcb68a]/35 rounded-xl p-4 mt-4 shadow-lg">
                                                        <div className="flex flex-wrap items-center justify-between gap-2 pb-3 mb-4 border-b border-[#dcb68a]/20">
                                                            <div className="flex items-center gap-2">
                                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-[10px] font-black uppercase tracking-widest bg-[#dcb68a]/15 text-[#dcb68a] border border-[#dcb68a]/40">
                                                                    AT — KV 14 GEHÄLTER
                                                                </span>
                                                                <h4 className="text-xs font-black uppercase tracking-widest text-[#dcb68a]">
                                                                    Österreich-Faktoren (13./14. Monatsgehalt &amp; DG-Abgaben)
                                                                </h4>
                                                            </div>
                                                            <span className="text-[11px] text-slate-400">
                                                                KV-Brutto × Aufschlag (inkl. Urlaubszuschuss &amp; Weihnachtsremuneration)
                                                            </span>
                                                        </div>

                                                        <div className="grid grid-cols-1 gap-y-3 md:grid-cols-2 md:gap-x-12">
                                                            {[
                                                                {
                                                                    key: 'At_Lohnnebenkosten_Fachkraft',
                                                                    label: 'Fachkraft Aufschlag (VZ & TZ)',
                                                                    value: Number(atValues.At_Lohnnebenkosten_Fachkraft) || 52.0,
                                                                },
                                                                {
                                                                    key: 'At_Lohnnebenkosten_Azubi',
                                                                    label: 'Lehrling / Azubi Aufschlag',
                                                                    value: Number(atValues.At_Lohnnebenkosten_Azubi) || 35.0,
                                                                },
                                                            ].map((item) => {
                                                                const factorVal = (1 + item.value / 100).toFixed(2).replace('.', ',');
                                                                return (
                                                                    <div
                                                                        key={`country-AT-${item.key}`}
                                                                        className="flex items-center justify-between py-1.5 hover:bg-white/5 rounded transition-colors px-2 -mx-2 group"
                                                                    >
                                                                        <div className="w-1/2 pr-4">
                                                                            <span className="text-xs font-bold uppercase tracking-wide text-gray-300 group-hover:text-white block">
                                                                                {item.label}
                                                                            </span>
                                                                            <span className="text-[10px] font-mono text-[#dcb68a]/85">
                                                                                Faktor: {factorVal}× auf KV-Bruttolohn
                                                                            </span>
                                                                        </div>
                                                                        <div className="w-40 h-10">
                                                                            <EditableField
                                                                                value={item.value}
                                                                                onChange={(val: any) =>
                                                                                    handleParameterChange('country', item.key, val, 'AT')
                                                                                }
                                                                                type="percentage"
                                                                                decimalPlaces={1}
                                                                                fieldKey={item.key}
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                )}

                                                {countryId === 'IT' && itValues && (
                                                    <div className="bg-[#15171e] border border-[#dcb68a]/35 rounded-xl p-4 mt-4 shadow-lg">
                                                        <div className="flex flex-wrap items-center justify-between gap-2 pb-3 mb-4 border-b border-[#dcb68a]/20">
                                                            <div className="flex items-center gap-2">
                                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-[10px] font-black uppercase tracking-widest bg-[#dcb68a]/15 text-[#dcb68a] border border-[#dcb68a]/40">
                                                                    IT_BZ
                                                                </span>
                                                                <h4 className="text-xs font-black uppercase tracking-widest text-[#dcb68a]">
                                                                    Südtirol-Faktoren (Lohn-Multiplikatoren IT_BZ)
                                                                </h4>
                                                            </div>
                                                            <span className="text-[11px] text-slate-400">
                                                                Netto-Multiplikator („Netto in busta“ → Costo Aziendale)
                                                            </span>
                                                        </div>

                                                        <div className="grid grid-cols-1 gap-y-3 md:grid-cols-2 md:gap-x-12">
                                                            {[
                                                                {
                                                                    key: 'ItBz_Faktor_Azubi',
                                                                    label: 'Lehrling / Azubi Faktor',
                                                                    value: Number(itValues.ItBz_Faktor_Azubi) || 1.42,
                                                                    isFactor: true,
                                                                },
                                                                {
                                                                    key: 'ItBz_Faktor_Teilzeit',
                                                                    label: `Teilzeit Faktor (< ${thresholdVal}h)`,
                                                                    value: Number(itValues.ItBz_Faktor_Teilzeit) || 1.65,
                                                                    isFactor: true,
                                                                },
                                                                {
                                                                    key: 'ItBz_Faktor_Vollzeit',
                                                                    label: `Vollzeit Faktor (>= ${thresholdVal}h)`,
                                                                    value: Number(itValues.ItBz_Faktor_Vollzeit) || 1.80,
                                                                    isFactor: true,
                                                                },
                                                                {
                                                                    key: 'ItBz_Vollzeit_Stundenschwelle',
                                                                    label: 'Vollzeit-Stundenschwelle (Std./Woche)',
                                                                    value: thresholdVal,
                                                                    isFactor: false,
                                                                },
                                                            ].map((item) => {
                                                                const pctAufschlag = item.isFactor
                                                                    ? Math.round((item.value - 1) * 100)
                                                                    : null;
                                                                return (
                                                                    <div
                                                                        key={`country-IT-${item.key}`}
                                                                        className="flex items-center justify-between py-1.5 hover:bg-white/5 rounded transition-colors px-2 -mx-2 group"
                                                                    >
                                                                        <div className="w-1/2 pr-4">
                                                                            <span className="text-xs font-bold uppercase tracking-wide text-gray-300 group-hover:text-white block">
                                                                                {item.label}
                                                                            </span>
                                                                            {item.isFactor && pctAufschlag !== null ? (
                                                                                <span className="text-[10px] font-mono text-[#dcb68a]/85">
                                                                                    Aufschlag: {pctAufschlag >= 0 ? `+${pctAufschlag} %` : `${pctAufschlag} %`} auf Netto
                                                                                </span>
                                                                            ) : (
                                                                                <span className="text-[10px] font-mono text-slate-400">
                                                                                    Grenze Teilzeit / Vollzeit (Std.)
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                        <div className="w-40 h-10">
                                                                            <EditableField
                                                                                value={item.value}
                                                                                onChange={(val: any) =>
                                                                                    handleParameterChange('country', item.key, val, 'IT')
                                                                                }
                                                                                type="number"
                                                                                decimalPlaces={item.isFactor ? 2 : 1}
                                                                                suffix={item.isFactor ? '×' : 'Std.'}
                                                                                fieldKey={item.key}
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </UI.CollapsibleSection>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            </UI.Section>
        </UI.Page>
    );
};
export default KalkulationsdatenPage;